/**
 * 飞书剧本页面 — 读取飞书表格原始内容
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  App,
  Badge,
  Button,
  Card,
  Collapse,
  Descriptions,
  Input,
  Modal,
  Popconfirm,
  Progress,
  Space,
  Spin,
  Select,
  Switch,
  Table,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import { GlobalAssignmentBlockDetail } from "../components/scriptSheet/GlobalAssignmentBlockDetail";
import { ScriptAssignmentReviewPanel } from "../components/scriptSheet/ScriptAssignmentReviewPanel";
import { FileTextOutlined, ReloadOutlined, ThunderboltOutlined, UserSwitchOutlined } from "../components/icons";
import { trpc } from "../trpc";
import { useTenantContext } from "../tenant/TenantContext";
import { useScriptAssistantAccess } from "../hooks/useScriptAssistantAccess";
import { isFeishuComplexCell, buildFeishuMediaProxyUrl } from "../../shared/feishuCellValue";
import { columnIndexToLetter } from "../../shared/feishuSheetUrl";
import {
  filterRoleColumnMerges,
  type FeishuMergeRange,
} from "../../shared/feishuSheetMerges";
import { runConcurrentTasks } from "../../shared/runConcurrentTasks";
import {
  collectScriptSheetImages,
  filterScriptSheetRows,
  getScriptSheetDataColumnLetters,
  IMAGE_UNDERSTANDING_CONCURRENCY,
  parseScriptSheetValues,
  SCRIPT_ATMOSPHERE_ROLE_PREFIX,
  SCRIPT_DISPATCHER_ROLE,
  SCRIPT_MAX_VISIBLE_COLUMN_INDEX,
  SCRIPT_ROLE_COLUMN_INDEX,
  type ParsedScriptSheetRow,
  type ScriptSheetImageRef,
} from "../../shared/feishuScriptSheet";
import {
  SCRIPT_CONTENT_COLUMN,
  buildAssignmentRowEligibility,
  buildScriptRoleBlockSummaries,
  collectAtmosphereContentImageTokens,
  formatScriptContentRowRange,
  shouldUseInlinePersona,
} from "../../shared/feishuScriptAssignment";
import type {
  RoleAssignmentItem,
  ScriptRolePersona,
  ScriptRoleSelectionSource,
} from "../../shared/scriptRoleAssignment";
import { SCRIPT_ROLE_SELECTION_SOURCE_LABELS } from "../../shared/scriptRoleAssignment";
import type { GlobalAssignmentBlockDiagnostics } from "../../shared/scriptRoleGlobalAssignmentDiagnostics";
import {
  SCRIPT_ROLE_PERSONA_TERM,
  formatScriptRoleMatchFailureError,
} from "../../shared/scriptRoleErrors";
import {
  getScriptContentBeatLabel,
  type ScriptContentBeatType,
} from "../../shared/scriptRoleDiversity";
import {
  addAiTokenUsage,
  emptyAiTokenUsage,
  formatTokenCount,
  type AiTokenUsage,
} from "../../shared/aiTokenUsage";
import {
  buildScriptAssignmentCacheLookup,
  isResumableScriptRoleMatchJob,
  type ScriptAssignmentMatchJobStatus,
  type ScriptAssignmentMatchProgressSnapshot,
  type ScriptAssignmentSessionCache,
  type ScriptAssignmentTempRole,
  type ScriptRoleAssignmentCacheKey,
  SCRIPT_ROLE_ASSIGNMENT_CONCURRENCY,
} from "../../shared/scriptRoleAssignmentCache";

interface AssignmentResult {
  spreadsheetToken: string;
  sheetId: string;
  group: string;
  overwriteMode: boolean;
  imageTextsByFileToken: Record<string, string>;
  tempRoles: Array<{
    tempRoleId: string;
    speakerLabel: string;
    utteranceCount: number;
    contentBeat: ScriptContentBeatType;
    firstRowIndex: number;
    lastRowIndex?: number;
    contentRowNumbers?: number[];
  }>;
  personas: Array<{
    tempRoleId: string;
    personaSummary: string;
    toneTags: string[];
    topicTags: string[];
    sampleQuotes: string[];
  }>;
  assignments: RoleAssignmentItem[];
  previewRows: Array<ParsedScriptSheetRow & { assignedRole: string }>;
  replacements: Array<{ rowIndex: number; value: string }>;
}

const { Text, Paragraph } = Typography;

interface ImageUnderstandingState {
  content: string;
  cached: boolean;
  understoodAt?: string;
}

interface ImageUnderstandingProgressState {
  open: boolean;
  total: number;
  done: number;
  success: number;
  fail: number;
  currentLabel: string;
  running: boolean;
  failures: Array<{ fileToken: string; label: string; error: string }>;
}

const INITIAL_IMAGE_PROGRESS: ImageUnderstandingProgressState = {
  open: false,
  total: 0,
  done: 0,
  success: 0,
  fail: 0,
  currentLabel: "",
  running: false,
  failures: [],
};

type AssignmentItemStatus =
  | "pending"
  | "inline_persona"
  | "persona"
  | "persona_ready"
  | "matching"
  | "done"
  | "skipped"
  | "error";

interface AssignmentTableRow extends RoleAssignmentItem {
  sampleQuotes: string[];
  contentRowNumbers: number[];
  contentRowRange: string;
}

interface AssignmentProgressItem {
  tempRoleId: string;
  speakerLabel: string;
  utteranceCount: number;
  contentBeat: ScriptContentBeatType;
  firstRowIndex: number;
  contentRowNumbers: number[];
  contentRowRange: string;
  status: AssignmentItemStatus;
  error?: string;
  blockDiagnostics?: GlobalAssignmentBlockDiagnostics;
  persona?: ScriptRolePersona;
  assignment?: RoleAssignmentItem;
}

function countCompletedPersonas(
  tempRoles: ScriptAssignmentTempRole[],
  personas: Record<string, ScriptRolePersona>
) {
  return tempRoles.filter((role) => Boolean(personas[role.tempRoleId])).length;
}

function areAllPersonasComplete(
  tempRoles: ScriptAssignmentTempRole[],
  personas: Record<string, ScriptRolePersona>
) {
  return tempRoles.length > 0 && countCompletedPersonas(tempRoles, personas) === tempRoles.length;
}

interface AssignmentTokenUsageState {
  persona: AiTokenUsage;
  match: AiTokenUsage;
  total: AiTokenUsage;
}

interface AssignmentProgressState {
  open: boolean;
  phase: "idle" | "prepare" | "persona" | "matching" | "done";
  running: boolean;
  memberCount: number;
  personaDone: number;
  matchDone: number;
  total: number;
  currentLabel: string;
  activeTempRoleId: string | null;
  activeSince: number | null;
  matchStartedAt: number | null;
  matchJobStatus?: ScriptAssignmentMatchJobStatus;
  matchScoringDone: number;
  matchSelectionDone: number;
  matchJobTotal: number;
  matchLastHeartbeatAt: string | null;
  matchPoll: {
    count: number;
    lastAt: number | null;
    lastError: string | null;
    startStatus: "idle" | "pending" | "accepted" | "failed";
    startError: string | null;
  };
  tokenUsage: AssignmentTokenUsageState;
  items: AssignmentProgressItem[];
}

interface AssignmentOverallStatus {
  title: string;
  alertType: "info" | "success" | "warning" | "error";
  alertMessage: string;
  alertDescription?: string;
  isLive: boolean;
}

function countAssignmentItemsByStatus(
  items: AssignmentProgressItem[],
  status: AssignmentItemStatus
) {
  return items.filter((item) => item.status === status).length;
}

function hasResumableAssignmentProgress(progress: AssignmentProgressState) {
  if (progress.total === 0) return false;
  if (progress.running) return true;
  const errorCount = countAssignmentItemsByStatus(progress.items, "error");
  const personasComplete =
    progress.total > 0 && progress.personaDone >= progress.total && errorCount === 0;
  const matchesComplete = progress.total > 0 && progress.matchDone >= progress.total;
  return errorCount > 0 || !personasComplete || !matchesComplete;
}

function countRetryablePersonaFailures(items: AssignmentProgressItem[]) {
  return items.filter(
    (item) => item.status === "error" && !shouldUseInlinePersona(item.utteranceCount)
  ).length;
}

function isAssignmentRowActive(
  record: AssignmentProgressItem,
  progress: AssignmentProgressState,
  assigning = false
) {
  if (!progress.running && !assigning) return false;
  if (progress.phase === "persona") {
    return record.status === "persona";
  }
  if (progress.phase === "matching") {
    return record.tempRoleId === progress.activeTempRoleId;
  }
  return false;
}

function resolveAssignmentOverallStatus(
  progress: AssignmentProgressState,
  assigning: boolean
): AssignmentOverallStatus {
  const errorCount = countAssignmentItemsByStatus(progress.items, "error");
  const doneCount = countAssignmentItemsByStatus(progress.items, "done");
  const personasComplete =
    progress.total > 0 && progress.personaDone >= progress.total;
  const matchesComplete = progress.total > 0 && progress.matchDone >= progress.total;
  const progressSummary = `画像 ${progress.personaDone}/${progress.total} · 匹配 ${progress.matchDone}/${progress.total}`;

  if (progress.running) {
    const phaseLabel: Record<AssignmentProgressState["phase"], string> = {
      idle: "空闲",
      prepare: "准备数据",
      persona: "画像分析",
      matching: "角色匹配",
      done: "收尾",
    };
    const step = phaseLabel[progress.phase];
    const activeRole = progress.activeTempRoleId
      ? `气氛员${progress.activeTempRoleId}`
      : progress.phase === "persona"
        ? `${countAssignmentItemsByStatus(progress.items, "persona")} 个并发处理中`
        : progress.phase === "matching" && progress.matchJobStatus === "scoring"
          ? `打分 ${progress.matchScoringDone}/${progress.matchJobTotal || progress.total}`
          : progress.phase === "matching" && progress.matchJobStatus === "selecting"
            ? `选择 ${progress.matchSelectionDone}/${progress.matchJobTotal || progress.total}`
            : null;
    return {
      title: `运行中 · ${step}`,
      alertType: "info",
      alertMessage: progress.currentLabel || `${step}进行中`,
      alertDescription: [progressSummary, activeRole ? `当前：${activeRole}` : null]
        .filter(Boolean)
        .join(" · "),
      isLive: true,
    };
  }

  if (assigning && progress.total > 0) {
    return {
      title: "后台运行中",
      alertType: "info",
      alertMessage: progress.currentLabel || "任务进行中",
      alertDescription: progress.open
        ? progressSummary
        : `${progressSummary}（弹窗已收起，可随时重新打开）`,
      isLive: true,
    };
  }

  if (matchesComplete) {
    return {
      title: "分配已完成",
      alertType: "success",
      alertMessage: `全部 ${progress.total} 个临时角色已匹配完成`,
      alertDescription: progressSummary,
      isLive: false,
    };
  }

  if (errorCount > 0 && !personasComplete) {
    return {
      title: "画像失败待重试",
      alertType: "error",
      alertMessage: `${errorCount} 项画像失败，请在进度窗口中重试`,
      alertDescription: progressSummary,
      isLive: false,
    };
  }

  if (personasComplete && progress.matchDone === 0) {
    return {
      title: "等待开始匹配",
      alertType: "warning",
      alertMessage: "画像已全部完成，请点击「开始匹配」",
      alertDescription: progressSummary,
      isLive: false,
    };
  }

  if (progress.matchDone > 0 && !matchesComplete) {
    return {
      title: errorCount > 0 ? "匹配中断" : "匹配未完成",
      alertType: errorCount > 0 ? "error" : "warning",
      alertMessage:
        progress.currentLabel ||
        `已完成 ${doneCount}/${progress.total}，${errorCount > 0 ? `${errorCount} 项失败` : "可继续匹配"}`,
      alertDescription: progressSummary,
      isLive: false,
    };
  }

  if (progress.total > 0) {
    return {
      title: "已暂停",
      alertType: "warning",
      alertMessage: progress.currentLabel || "任务已暂停，可继续操作",
      alertDescription: progressSummary,
      isLive: false,
    };
  }

  return {
    title: "未开始",
    alertType: "info",
    alertMessage: "尚未开始分配",
    isLive: false,
  };
}

function buildInitialAssignmentTokenUsage(): AssignmentTokenUsageState {
  return {
    persona: emptyAiTokenUsage(),
    match: emptyAiTokenUsage(),
    total: emptyAiTokenUsage(),
  };
}

function mergeAssignmentTokenUsage(
  current: AssignmentTokenUsageState,
  phase: "persona" | "match",
  usage: AiTokenUsage
): AssignmentTokenUsageState {
  const nextPhaseUsage = addAiTokenUsage(current[phase], usage);
  const persona = phase === "persona" ? nextPhaseUsage : current.persona;
  const match = phase === "match" ? nextPhaseUsage : current.match;
  return {
    persona,
    match,
    total: addAiTokenUsage(persona, match),
  };
}

function computeMatchProgressPercent(progress: AssignmentProgressState): number {
  if (progress.total <= 0) return 0;
  if (
    progress.phase === "matching" &&
    progress.matchJobTotal > 0 &&
    progress.matchJobStatus
  ) {
    if (progress.matchJobStatus === "scoring") {
      return Math.round((progress.matchScoringDone / progress.matchJobTotal) * 100);
    }
    if (progress.matchJobStatus === "selecting") {
      return Math.round((progress.matchSelectionDone / progress.matchJobTotal) * 100);
    }
  }
  return Math.round((progress.matchDone / progress.total) * 100);
}

function formatMatchProgressSummary(progress: AssignmentProgressState): string {
  if (
    progress.phase === "matching" &&
    progress.matchJobTotal > 0 &&
    progress.matchJobStatus === "scoring"
  ) {
    return `打分 ${progress.matchScoringDone} / ${progress.matchJobTotal}`;
  }
  if (
    progress.phase === "matching" &&
    progress.matchJobTotal > 0 &&
    progress.matchJobStatus === "selecting"
  ) {
    return `选择 ${progress.matchSelectionDone} / ${progress.matchJobTotal} · 已分配 ${progress.matchDone} / ${progress.total}`;
  }
  return `${progress.matchDone} / ${progress.total}`;
}

function resolveNextMatchingTempRoleId(
  tempRoles: ScriptAssignmentTempRole[],
  cache: ScriptAssignmentSessionCache,
  failureMap: Map<string, string>
): string | null {
  const assignedIds = new Set(cache.assignments.map((item) => item.tempRoleId));
  const next = [...tempRoles]
    .sort((left, right) => left.firstRowIndex - right.firstRowIndex)
    .find(
      (role) => !assignedIds.has(role.tempRoleId) && !failureMap.has(role.tempRoleId)
    );
  return next?.tempRoleId ?? null;
}

function mapMatchProgressItems(
  items: AssignmentProgressItem[],
  cache: ScriptAssignmentSessionCache,
  matchJob: ScriptAssignmentSessionCache["matchJob"],
  activeTempRoleId: string | null
): AssignmentProgressItem[] {
  const failureByRoleId = new Map(
    (matchJob?.failures ?? []).map((item) => [item.tempRoleId, item])
  );
  const assignedIds = new Set(cache.assignments.map((item) => item.tempRoleId));

  return items.map((row) => {
    const match = cache.assignments.find((a) => a.tempRoleId === row.tempRoleId);
    if (match) {
      return {
        ...row,
        status: "done" as const,
        assignment: match,
        error: undefined,
        blockDiagnostics: undefined,
      };
    }
    const failure = failureByRoleId.get(row.tempRoleId);
    if (failure) {
      return {
        ...row,
        status: failure.skipped ? ("skipped" as const) : ("error" as const),
        error: failure.error,
        blockDiagnostics: failure.blockDiagnostics,
      };
    }
    if (!assignedIds.has(row.tempRoleId) && matchJob) {
      if (matchJob.status === "pending" || matchJob.status === "scoring") {
        return { ...row, status: "matching" as const, error: undefined };
      }
      if (matchJob.status === "selecting") {
        if (row.tempRoleId === activeTempRoleId) {
          return { ...row, status: "matching" as const, error: undefined };
        }
        if (row.status === "done" || row.status === "error") return row;
        return { ...row, status: "persona_ready" as const, error: undefined };
      }
    }
    return row;
  });
}

function progressAssignmentsToFull(
  items: ScriptAssignmentMatchProgressSnapshot["assignments"]
): RoleAssignmentItem[] {
  return items.map((item) => ({
    ...item,
    scoreBreakdown: [],
    selectionSource: item.selectionSource,
  }));
}

function summarizeSelectionSources(assignments: RoleAssignmentItem[]) {
  let ranked = 0;
  let fallback = 0;
  let global = 0;
  let globalExpanded = 0;
  for (const item of assignments) {
    if (item.selectionSource === "fallback") fallback += 1;
    else if (item.selectionSource === "ranked") ranked += 1;
    else if (item.selectionSource === "global") global += 1;
    else if (item.selectionSource === "global_expanded") globalExpanded += 1;
  }
  return { ranked, fallback, global, globalExpanded };
}

function formatSelectionSourceSummary(assignments: RoleAssignmentItem[]) {
  const { ranked, fallback, global, globalExpanded } =
    summarizeSelectionSources(assignments);
  if (ranked === 0 && fallback === 0 && global === 0 && globalExpanded === 0) return "";
  const parts: string[] = [];
  if (global > 0) parts.push(`全局分配 ${global}`);
  if (globalExpanded > 0) parts.push(`扩容分配 ${globalExpanded}`);
  if (ranked > 0) parts.push(`规则直选 ${ranked}`);
  if (fallback > 0) parts.push(`二次 LLM ${fallback}`);
  return parts.join(" · ");
}

function renderSelectionSourceTag(source?: ScriptRoleSelectionSource) {
  if (!source) {
    return (
      <Tooltip title="历史匹配结果，无来源标记">
        <Tag>—</Tag>
      </Tooltip>
    );
  }
  return (
    <Tag color={source === "ranked" ? "success" : "warning"}>
      {SCRIPT_ROLE_SELECTION_SOURCE_LABELS[source]}
    </Tag>
  );
}

function snapshotToSessionView(
  snapshot: ScriptAssignmentMatchProgressSnapshot,
  input: {
    cacheLookup: ScriptRoleAssignmentCacheKey;
    tempRoles: ScriptAssignmentTempRole[];
  }
): ScriptAssignmentSessionCache {
  return {
    spreadsheetToken: input.cacheLookup.spreadsheetToken,
    sheetId: input.cacheLookup.sheetId,
    group: input.cacheLookup.group,
    sourceFingerprint: input.cacheLookup.sourceFingerprint,
    imageTextsByFileToken: {},
    tempRoles: input.tempRoles,
    memberCount: snapshot.memberCount,
    personas: {},
    assignments: progressAssignmentsToFull(snapshot.assignments),
    matchJob: snapshot.matchJob ?? undefined,
    updatedAt: snapshot.updatedAt ?? new Date().toISOString(),
  };
}

function buildMatchProgressSignature(snapshot: ScriptAssignmentMatchProgressSnapshot): string {
  const job = snapshot.matchJob;
  return [
    job?.status ?? "none",
    job?.scoringDone ?? 0,
    job?.selectionDone ?? 0,
    snapshot.assignmentCount,
    job?.lastHeartbeatAt ?? snapshot.updatedAt ?? "",
    job?.lastActivityTempRoleId ?? "",
  ].join(":");
}

function resolveMatchJobLastActivityAt(snapshot: ScriptAssignmentMatchProgressSnapshot): number {
  const job = snapshot.matchJob;
  const raw = job?.lastHeartbeatAt ?? snapshot.updatedAt;
  if (!raw) return 0;
  const parsed = Date.parse(raw);
  return Number.isFinite(parsed) ? parsed : 0;
}

/**
 * Polls lightweight match progress until batch matching completes.
 */
async function waitForMatchProgressViaPolling(input: {
  cacheLookup: ScriptRoleAssignmentCacheKey;
  tempRoles: ScriptAssignmentTempRole[];
  targetAssignmentCount: number;
  getEarlyError?: () => string | null;
  setAssignmentProgress: (
    updater: (prev: AssignmentProgressState) => AssignmentProgressState
  ) => void;
  setAssignmentResult: (
    updater: (prev: AssignmentResult | null) => AssignmentResult | null
  ) => void;
  result: SheetReadResult;
  selectedGroup: string;
  parsedRows: ParsedScriptSheetRow[];
  overwriteMode: boolean;
  imageTextsByFileToken: Record<string, string>;
}): Promise<ScriptAssignmentSessionCache> {
  const MATCH_POLL_INTERVAL_MS = 1000;
  const MATCH_POLL_TIMEOUT_MS = 25 * 60 * 1000;
  const MATCH_STALL_MS = 3 * 60 * 1000;

  const applyProgressToUi = (
    snapshot: ScriptAssignmentMatchProgressSnapshot,
    pollMeta: { count: number; lastError: string | null }
  ) => {
    const cache = snapshotToSessionView(snapshot, {
      cacheLookup: input.cacheLookup,
      tempRoles: input.tempRoles,
    });
    const matchJob = cache.matchJob;
    const doneCount = cache.assignments.length;
    const failureMap = new Map(
      (matchJob?.failures ?? []).map((item) => [item.tempRoleId, item.error])
    );
    const activeTempRoleId =
      matchJob?.status === "selecting"
        ? resolveNextMatchingTempRoleId(cache.tempRoles, cache, failureMap)
        : matchJob?.lastActivityTempRoleId ?? null;

    let currentLabel = `批量匹配中…（${doneCount}/${input.targetAssignmentCount}）`;
    if (matchJob?.status === "pending") {
      currentLabel = `匹配任务已受理，准备打分…（0/${matchJob.totalRoles}）`;
    } else if (matchJob?.status === "scoring") {
      currentLabel = `并发打分中…（${matchJob.scoringDone}/${matchJob.totalRoles}）`;
      if (matchJob.lastActivityTempRoleId) {
        currentLabel += ` · 最近：气氛员${matchJob.lastActivityTempRoleId}`;
      }
    } else if (matchJob?.status === "selecting") {
      currentLabel = `多样性选择中…（${matchJob.selectionDone}/${matchJob.totalRoles}）`;
      if (activeTempRoleId) {
        currentLabel += ` · 当前：气氛员${activeTempRoleId}`;
      }
    } else if (matchJob?.status === "completed") {
      currentLabel =
        (matchJob.failures.length ?? 0) > 0
          ? `批量匹配完成，${matchJob.failures.length} 项失败`
          : `批量匹配完成（${doneCount}/${input.targetAssignmentCount}）`;
    } else if (matchJob?.status === "failed") {
      currentLabel = matchJob.error ?? "批量匹配失败";
    } else if (!snapshot.found) {
      currentLabel = "服务器未找到分配缓存";
    } else if (!snapshot.fingerprintMatched) {
      currentLabel = "剧本内容已变更，请重新读取表格后再匹配";
    }

    const sourceSummary = formatSelectionSourceSummary(cache.assignments);
    if (
      sourceSummary &&
      matchJob &&
      (matchJob.status === "selecting" || matchJob.status === "completed")
    ) {
      currentLabel = `${currentLabel} · ${sourceSummary}`;
    }

    input.setAssignmentProgress((prev) => ({
      ...prev,
      open: true,
      running: true,
      phase: "matching",
      matchDone: doneCount,
      matchJobStatus: matchJob?.status,
      matchScoringDone: matchJob?.scoringDone ?? prev.matchScoringDone,
      matchSelectionDone: matchJob?.selectionDone ?? prev.matchSelectionDone,
      matchJobTotal: matchJob?.totalRoles ?? prev.matchJobTotal,
      matchLastHeartbeatAt: matchJob?.lastHeartbeatAt ?? snapshot.updatedAt ?? null,
      activeTempRoleId:
        matchJob?.status === "pending" ||
        matchJob?.status === "scoring" ||
        matchJob?.status === "selecting"
          ? activeTempRoleId
          : null,
      currentLabel,
      matchPoll: {
        ...prev.matchPoll,
        count: pollMeta.count,
        lastAt: Date.now(),
        lastError: pollMeta.lastError,
      },
      items: mapMatchProgressItems(prev.items, cache, matchJob, activeTempRoleId),
    }));

    input.setAssignmentResult((prev) => ({
      spreadsheetToken: input.result.spreadsheetToken,
      sheetId: input.result.sheetId,
      group: input.selectedGroup,
      overwriteMode: input.overwriteMode,
      imageTextsByFileToken: input.imageTextsByFileToken,
      tempRoles: cache.tempRoles,
      personas: prev?.personas ?? [],
      assignments: [...cache.assignments],
      previewRows:
        prev?.previewRows ?? input.parsedRows.map((row) => ({ ...row, assignedRole: row.role })),
      replacements: prev?.replacements ?? [],
    }));
  };

  return new Promise((resolve, reject) => {
    let active = true;
    let pollCount = 0;
    let pollsWithoutJob = 0;
    const pollStartedAt = Date.now();
    let lastProgressSignature = "";
    let lastProgressChangeAt = Date.now();
    let pollInFlight = false;
    let interval: number | undefined;

    const stopPolling = () => {
      active = false;
      if (interval !== undefined) window.clearInterval(interval);
    };

    const poll = async () => {
      if (!active || pollInFlight) return;
      pollInFlight = true;
      pollCount += 1;

      const earlyError = input.getEarlyError?.();
      if (earlyError) {
        stopPolling();
        reject(new Error(earlyError));
        pollInFlight = false;
        return;
      }

      if (Date.now() - pollStartedAt > MATCH_POLL_TIMEOUT_MS) {
        stopPolling();
        reject(new Error("匹配超时（25 分钟），请查看服务器日志或稍后重试"));
        pollInFlight = false;
        return;
      }

      try {
        const snapshot = await trpc.feishuSheets.getRoleAssignmentMatchProgress.query(
          input.cacheLookup
        );
        if (!active) return;

        let pollError: string | null = null;
        if (!snapshot.found) {
          pollError = "服务器未找到分配缓存";
          pollsWithoutJob += 1;
        } else if (!snapshot.fingerprintMatched) {
          pollError = "剧本指纹不匹配，请重新读取表格";
          stopPolling();
          reject(new Error(pollError));
          return;
        } else if (!snapshot.matchJob) {
          pollsWithoutJob += 1;
          pollError =
            pollsWithoutJob >= 30
              ? "匹配任务超过 30 秒未启动，请检查网络或服务端日志"
              : null;
        } else {
          pollsWithoutJob = 0;
          const signature = buildMatchProgressSignature(snapshot);
          const lastActivityAt = resolveMatchJobLastActivityAt(snapshot);
          if (signature !== lastProgressSignature) {
            lastProgressSignature = signature;
            lastProgressChangeAt = Date.now();
          } else if (
            ["pending", "scoring", "selecting"].includes(snapshot.matchJob.status) &&
            lastActivityAt > 0 &&
            Date.now() - lastActivityAt > MATCH_STALL_MS
          ) {
            const heartbeatLabel = snapshot.matchJob.lastHeartbeatAt
              ? new Date(snapshot.matchJob.lastHeartbeatAt).toLocaleTimeString("zh-CN")
              : "未知";
            pollError = `服务端心跳停在 ${heartbeatLabel}，已超过 ${Math.floor(MATCH_STALL_MS / 60000)} 分钟无进展。请重新点击「匹配」自动恢复僵死任务，并查看服务端 [script-role-match] 日志（非浏览器控制台）`;
          }
        }

        applyProgressToUi(snapshot, { count: pollCount, lastError: pollError });

        if ((!snapshot.found && pollsWithoutJob >= 3) || pollsWithoutJob >= 30 || pollError?.includes("服务端心跳停在")) {
          stopPolling();
          reject(new Error(pollError || "匹配任务未启动"));
          return;
        }

        const jobStatus = snapshot.matchJob?.status;
        if (jobStatus === "completed" || jobStatus === "failed") {
          try {
            const fullCache = await trpc.feishuSheets.getRoleAssignmentCache.query(
              input.cacheLookup
            );
            if (!fullCache) {
              throw new Error("匹配已完成但无法加载完整缓存");
            }
            stopPolling();
            resolve(fullCache);
          } catch (error: unknown) {
            stopPolling();
            reject(
              error instanceof Error
                ? error
                : new Error("匹配已完成但无法加载完整缓存")
            );
          }
        }
      } catch (error: unknown) {
        if (!active) return;
        const message = error instanceof Error ? error.message : "进度查询失败";
        input.setAssignmentProgress((prev) => ({
          ...prev,
          matchPoll: {
            ...prev.matchPoll,
            count: pollCount,
            lastAt: Date.now(),
            lastError: message,
          },
        }));
        if (Date.now() - lastProgressChangeAt > MATCH_STALL_MS) {
          stopPolling();
          reject(new Error(message));
        }
      } finally {
        pollInFlight = false;
      }
    };

    interval = window.setInterval(() => {
      void poll();
    }, MATCH_POLL_INTERVAL_MS);
    void poll();
  });
}

function renderAssignmentTokenUsage(tokenUsage: AssignmentTokenUsageState) {
  if (tokenUsage.total.requestCount === 0) {
    return <Text type="secondary">Token 统计：暂无数据</Text>;
  }

  const { total, persona, match } = tokenUsage;
  return (
    <Space vertical size={4} style={{ width: "100%" }}>
      <Text type="secondary">
        Token 统计 · 请求 {formatTokenCount(total.requestCount)} 次
      </Text>
      <Text>
        输入 {formatTokenCount(total.inputTokens)} · 输出 {formatTokenCount(total.outputTokens)} ·
        缓存命中 {formatTokenCount(total.cachedInputTokens)} · 合计{" "}
        {formatTokenCount(total.totalTokens)}
      </Text>
      <Text type="secondary" style={{ fontSize: 12 }}>
        画像：输入 {formatTokenCount(persona.inputTokens)} / 输出{" "}
        {formatTokenCount(persona.outputTokens)} ｜ 匹配：输入{" "}
        {formatTokenCount(match.inputTokens)} / 输出 {formatTokenCount(match.outputTokens)}
      </Text>
    </Space>
  );
}

const INITIAL_ASSIGNMENT_PROGRESS: AssignmentProgressState = {
  open: false,
  phase: "idle",
  running: false,
  memberCount: 0,
  personaDone: 0,
  matchDone: 0,
  total: 0,
  currentLabel: "",
  activeTempRoleId: null,
  activeSince: null,
  matchStartedAt: null,
  matchScoringDone: 0,
  matchSelectionDone: 0,
  matchJobTotal: 0,
  matchLastHeartbeatAt: null,
  matchPoll: {
    count: 0,
    lastAt: null,
    lastError: null,
    startStatus: "idle",
    startError: null,
  },
  tokenUsage: buildInitialAssignmentTokenUsage(),
  items: [],
};

interface ScriptSheetTableRow {
  key: string;
  __row: string;
  role: string;
  assignedRole?: string;
  roleIsInherited: boolean;
  tempRoleId?: string;
  blockLabel?: string;
  cellImages: Record<string, string>;
  [column: string]: string | boolean | Record<string, string> | undefined;
}

function renderAssignmentStatusTag(status: AssignmentItemStatus) {
  switch (status) {
    case "pending":
      return <Tag>等待</Tag>;
    case "inline_persona":
      return <Tag color="default">原话画像</Tag>;
    case "persona":
      return <Tag color="processing">画像分析中</Tag>;
    case "persona_ready":
      return <Tag color="cyan">画像完成</Tag>;
    case "matching":
      return <Tag color="processing">匹配中</Tag>;
    case "done":
      return <Tag color="success">已匹配</Tag>;
    case "skipped":
      return <Tag color="warning">待人工处理</Tag>;
    case "error":
      return <Tag color="error">失败</Tag>;
    default:
      return <Tag>—</Tag>;
  }
}

function renderAssignmentItemStatus(
  record: AssignmentProgressItem,
  progress: AssignmentProgressState,
  assigning = false
) {
  const isActive = isAssignmentRowActive(record, progress, assigning);
  return (
    <Space size={4} wrap>
      {renderAssignmentStatusTag(record.status)}
      {isActive ? <Badge status="processing" text="进行中" /> : null}
    </Space>
  );
}

function formatElapsedSeconds(activeSince: number | null, now = Date.now()) {
  if (!activeSince) return 0;
  return Math.max(0, Math.floor((now - activeSince) / 1000));
}

function renderMatchFailureDetail(record: AssignmentProgressItem) {
  if (!record.error && !record.blockDiagnostics) {
    return "—";
  }
  if (record.blockDiagnostics) {
    return (
      <Collapse
        size="small"
        items={[
          {
            key: "detail",
            label: (
              <Text type="danger" style={{ whiteSpace: "pre-wrap" }}>
                {record.error}
              </Text>
            ),
            children: <GlobalAssignmentBlockDetail diagnostics={record.blockDiagnostics} />,
          },
        ]}
      />
    );
  }
  return (
    <Text type="danger" style={{ whiteSpace: "pre-wrap" }}>
      {record.error}
    </Text>
  );
}

function renderMatchingInProgressLabel(activeSince: number | null, now: number) {
  const elapsed = formatElapsedSeconds(activeSince, now);
  return (
    <Space size={4}>
      <Spin size="small" />
      <Text type="secondary">正在匹配…（已耗时 {elapsed} 秒）</Text>
    </Space>
  );
}

function buildAssignmentProgressItems(
  tempRoles: ScriptAssignmentTempRole[],
  cache?: ScriptAssignmentSessionCache | null
): AssignmentProgressItem[] {
  const assignmentMap = new Map(
    (cache?.assignments ?? []).map((item) => [item.tempRoleId, item])
  );

  return tempRoles.map((role) => {
    const persona = cache?.personas[role.tempRoleId];
    const assignment = assignmentMap.get(role.tempRoleId);
    let status: AssignmentItemStatus = "pending";
    if (assignment) {
      status = "done";
    } else if (persona) {
      status = shouldUseInlinePersona(role.utteranceCount) ? "inline_persona" : "persona_ready";
    }

    return {
      tempRoleId: role.tempRoleId,
      speakerLabel: role.speakerLabel,
      utteranceCount: role.utteranceCount,
      contentBeat: role.contentBeat ?? "general",
      firstRowIndex: role.firstRowIndex ?? 0,
      contentRowNumbers: role.contentRowNumbers ?? [],
      contentRowRange: formatScriptContentRowRange(role.contentRowNumbers ?? []),
      status,
      persona,
      assignment,
    };
  });
}

async function persistAssignmentCache(
  lookup: ScriptRoleAssignmentCacheKey,
  payload: ScriptAssignmentSessionCache
) {
  return trpc.feishuSheets.saveRoleAssignmentCache.mutate({
    ...lookup,
    spreadsheetToken: payload.spreadsheetToken,
    sheetId: payload.sheetId,
    group: lookup.group,
    sourceFingerprint: lookup.sourceFingerprint,
    imageTextsByFileToken: payload.imageTextsByFileToken,
    tempRoles: payload.tempRoles,
    memberCount: payload.memberCount,
    personas: payload.personas,
    assignments: payload.assignments,
    updatedAt: payload.updatedAt ?? new Date().toISOString(),
  });
}

function renderSampleQuotes(quotes?: string[]) {
  if (!quotes?.length) {
    return <Text type="secondary">—</Text>;
  }

  return (
    <div style={{ fontSize: 12, lineHeight: 1.6 }}>
      {quotes.map((quote, index) => (
        <div
          key={`${index}-${quote.slice(0, 12)}`}
          style={{ whiteSpace: "pre-wrap", wordBreak: "break-word", marginBottom: quotes.length > 1 ? 6 : 0 }}
        >
          {quotes.length > 1 ? `${index + 1}. ` : ""}
          {quote}
        </div>
      ))}
    </div>
  );
}

function renderAssignedMemberLabel(input: { number?: string; nickname?: string }) {
  if (input.number?.trim()) {
    return (
      <span>
        <Text strong>{input.number}</Text>
        {input.nickname?.trim() ? (
          <Text type="secondary" style={{ marginLeft: 8, fontSize: 12 }}>
            {input.nickname}
          </Text>
        ) : null}
      </span>
    );
  }

  return <Text>{input.nickname?.trim() || "—"}</Text>;
}

function renderScriptSheetCell(
  value: string,
  imageFileToken?: string,
  understanding?: ImageUnderstandingState
) {
  if (imageFileToken) {
    return (
      <div>
        <img
          src={buildFeishuMediaProxyUrl(imageFileToken)}
          alt=""
          loading="lazy"
          style={{
            display: "block",
            maxWidth: 160,
            maxHeight: 160,
            objectFit: "contain",
            borderRadius: 4,
          }}
        />
        {understanding?.content ? (
          <Tooltip title={understanding.content}>
            <div style={{ marginTop: 6, fontSize: 12, color: "#64748b", lineHeight: 1.5 }}>
              {understanding.cached ? (
                <Tag color="blue" style={{ marginRight: 4 }}>
                  已缓存
                </Tag>
              ) : null}
              <span
                style={{
                  display: "-webkit-box",
                  WebkitLineClamp: 4,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                }}
              >
                {understanding.content}
              </span>
            </div>
          </Tooltip>
        ) : null}
      </div>
    );
  }

  if (!value) return "—";

  return (
    <span style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>{value}</span>
  );
}

function renderScoreBreakdownPanel(record: RoleAssignmentItem) {
  const breakdown = record.scoreBreakdown ?? [];
  if (breakdown.length === 0) {
    return <Text type="secondary">暂无评分明细</Text>;
  }

  const rawTotal = breakdown.reduce((sum, item) => sum + item.score, 0);

  return (
    <div style={{ padding: "8px 0" }}>
      <Table
        size="small"
        pagination={false}
        rowKey="key"
        dataSource={breakdown}
        columns={[
          {
            title: "维度",
            dataIndex: "label",
            width: 180,
            render: (label: string, row) =>
              row.key === "riskPenalty" ? <Text type="danger">{label}</Text> : label,
          },
          {
            title: "得分",
            dataIndex: "score",
            width: 80,
            render: (value: number, row) =>
              row.key === "riskPenalty" ? (
                <Text type="danger">{value}</Text>
              ) : (
                value
              ),
          },
          {
            title: "满分",
            dataIndex: "maxScore",
            width: 80,
            render: (value: number) => (value < 0 ? value : value),
          },
          {
            title: "说明",
            dataIndex: "reason",
            render: (value: string, row) =>
              row.key === "riskPenalty" ? (
                <Text type="danger">{value}</Text>
              ) : (
                value
              ),
          },
        ]}
      />
      <Text type="secondary" style={{ display: "block", marginTop: 8 }}>
        原始合计 {rawTotal} 分，汇总得分（截断至 0–100）：
        <Text strong style={{ marginLeft: 8 }}>
          {record.score}
        </Text>
      </Text>
    </div>
  );
}

interface SheetReadResult {
  spreadsheetToken: string;
  sheetId: string;
  sheetTitle: string;
  range: string;
  rowCount: number;
  columnCount: number;
  values: string[][];
  rawValues: unknown[][];
  roleColumnMerges: FeishuMergeRange[];
}

export function ScriptSheetPage() {
  const { message } = App.useApp();
  const { access: scriptAccess } = useScriptAssistantAccess();
  const { isAdmin } = useTenantContext();
  const [url, setUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const [credentialsLoading, setCredentialsLoading] = useState(true);
  const [hasCredentials, setHasCredentials] = useState(false);
  const [result, setResult] = useState<SheetReadResult | null>(null);
  const [hasImageUnderstandingModel, setHasImageUnderstandingModel] = useState(false);
  const [imageUnderstandings, setImageUnderstandings] = useState<
    Record<string, ImageUnderstandingState>
  >({});
  const [imageProgress, setImageProgress] = useState<ImageUnderstandingProgressState>(
    INITIAL_IMAGE_PROGRESS
  );
  const [groups, setGroups] = useState<string[]>([]);
  const [groupsLoading, setGroupsLoading] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState<string | undefined>();
  const [overwriteMode, setOverwriteMode] = useState(false);
  const [assigning, setAssigning] = useState(false);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [reviewWriting, setReviewWriting] = useState(false);
  const [reviewReceipt, setReviewReceipt] = useState<{
    reviewId: string;
    updatedCells: number;
    changedRoleCount: number;
  } | null>(null);
  const [retryingPersonaTempRoleId, setRetryingPersonaTempRoleId] = useState<string | null>(null);
  const [personaRetryRunning, setPersonaRetryRunning] = useState(false);
  const [assignmentResult, setAssignmentResult] = useState<AssignmentResult | null>(null);
  const [showAssignedPreview, setShowAssignedPreview] = useState(false);
  const [assignmentProgress, setAssignmentProgress] = useState<AssignmentProgressState>(
    INITIAL_ASSIGNMENT_PROGRESS
  );
  const [assignmentNowTick, setAssignmentNowTick] = useState(() => Date.now());
  const [serverAssignmentCache, setServerAssignmentCache] =
    useState<ScriptAssignmentSessionCache | null>(null);
  const completedCacheRestoredRef = useRef<string | null>(null);
  const autoOpenedReviewKeyRef = useRef<string | null>(null);
  const assignmentRunActiveRef = useRef(false);
  const [tablePagination, setTablePagination] = useState({ current: 1, pageSize: 50 });

  const tryReopenActiveAssignmentProgress = useCallback(() => {
    if (!assignmentRunActiveRef.current) return false;
    setAssignmentProgress((prev) => {
      if (prev.total === 0) return prev;
      return { ...prev, open: true };
    });
    return true;
  }, []);

  const closeAssignmentProgressModal = useCallback(() => {
    setAssignmentProgress((prev) => {
      if (prev.running || assignmentRunActiveRef.current) {
        return { ...prev, open: false };
      }
      if (hasResumableAssignmentProgress(prev)) {
        return { ...prev, open: false };
      }
      return INITIAL_ASSIGNMENT_PROGRESS;
    });
  }, []);

  const openAssignmentProgressModal = useCallback(() => {
    setAssignmentProgress((prev) => {
      if (prev.total === 0) return prev;
      return { ...prev, open: true };
    });
  }, []);

  const loadGroups = useCallback(async () => {
    setGroupsLoading(true);
    try {
      let data = await trpc.atmosphereMember.groups.query();
      if (!data.length) {
        const members = await trpc.atmosphereMember.list.query();
        data = [
          ...new Set(
            members
              .map((member) => member.group?.trim())
              .filter((group): group is string => Boolean(group))
          ),
        ].sort((a, b) => a.localeCompare(b, "zh-CN"));
      }
      setGroups(data);
      if (!data.length) {
        message.warning("暂无气氛员分组，请先在气氛员管理中为成员设置分组");
      }
    } catch (error: any) {
      setGroups([]);
      message.error(error?.message || "加载气氛员分组失败");
    } finally {
      setGroupsLoading(false);
    }
  }, [message]);

  useEffect(() => {
    trpc.setting
      .getFeishuApiSettings.query()
      .then((settings) => {
        setHasCredentials(Boolean(settings.appId && settings.hasAppSecret));
      })
      .catch(() => setHasCredentials(false))
      .finally(() => setCredentialsLoading(false));

    trpc.aiModel
      .getImageUnderstandingStatus.query()
      .then((status) => setHasImageUnderstandingModel(Boolean(status.modelId)))
      .catch(() => setHasImageUnderstandingModel(false));

    void loadGroups();
  }, [loadGroups]);

  const handleRead = async () => {
    if (!url.trim()) {
      message.warning("请输入飞书表格链接");
      return;
    }

    setLoading(true);
    try {
      const data = await trpc.feishuSheets.readByUrl.mutate({ url: url.trim() });
      setResult(data);
      setAssignmentResult(null);
      setShowAssignedPreview(false);
      setReviewOpen(false);
      setReviewWriting(false);
      setReviewReceipt(null);
      autoOpenedReviewKeyRef.current = null;
      void loadGroups();
      message.success("读取成功");
    } catch (error: any) {
      message.error(error?.message || "读取失败");
    } finally {
      setLoading(false);
    }
  };

  const { parsedRows, totalParsedRows, allParsedRows, scriptRoleBlocks } = useMemo(() => {
    if (!result?.values.length) {
      return {
        parsedRows: [],
        totalParsedRows: 0,
        allParsedRows: [],
        scriptRoleBlocks: [],
      };
    }
    const merges = filterRoleColumnMerges(result.roleColumnMerges ?? []);
    const allRows = parseScriptSheetValues(
      result.values,
      result.rawValues,
      merges
    );
    return {
      allParsedRows: allRows,
      parsedRows: filterScriptSheetRows(allRows),
      totalParsedRows: allRows.length,
      scriptRoleBlocks: buildScriptRoleBlockSummaries(allRows, merges, { overwriteMode }),
    };
  }, [result, overwriteMode]);

  const rowEligibility = useMemo(
    () => buildAssignmentRowEligibility(allParsedRows, { overwriteMode }),
    [allParsedRows, overwriteMode]
  );

  const documentImages = useMemo(() => {
    if (!result?.rawValues?.length) return [] as ScriptSheetImageRef[];
    return collectScriptSheetImages(parsedRows, result.rawValues);
  }, [parsedRows, result]);

  /** 匹配/画像所需的 C 列气氛员图片（与 ensureImageTexts 一致） */
  const assignmentContentImages = useMemo(() => {
    if (!result?.rawValues?.length) return [] as ScriptSheetImageRef[];
    const requiredTokens = new Set(
      collectAtmosphereContentImageTokens(parsedRows, { overwriteMode })
    );
    if (!requiredTokens.size) return [] as ScriptSheetImageRef[];
    return collectScriptSheetImages(parsedRows, result.rawValues).filter(
      (item) => item.column === SCRIPT_CONTENT_COLUMN && requiredTokens.has(item.fileToken)
    );
  }, [parsedRows, result, overwriteMode]);

  useEffect(() => {
    if (!result || assignmentContentImages.length === 0) {
      return;
    }

    const fileTokens = assignmentContentImages.map((item) => item.fileToken);
    let cancelled = false;
    void trpc.feishuSheets
      .getImageUnderstandingCache.query({ fileTokens })
      .then((cache) => {
        if (cancelled) return;
        setImageUnderstandings((prev) => {
          const next = { ...prev };
          for (const [fileToken, item] of Object.entries(cache.items)) {
            if (!item.content?.trim()) continue;
            next[fileToken] = {
              content: item.content,
              cached: true,
              understoodAt: item.understoodAt,
            };
          }
          return next;
        });
      })
      .catch(() => {
        // 保留已有本地理解结果，勿清空
      });

    return () => {
      cancelled = true;
    };
  }, [result, assignmentContentImages]);

  const handleUnderstandImages = async () => {
    if (!result || assignmentContentImages.length === 0) {
      message.warning("当前剧本没有匹配所需的 C 列图片");
      return;
    }

    setImageProgress({
      open: true,
      total: assignmentContentImages.length,
      done: 0,
      success: 0,
      fail: 0,
      currentLabel: "",
      running: true,
      failures: [],
    });

    try {
      await runConcurrentTasks({
        items: assignmentContentImages,
        concurrency: IMAGE_UNDERSTANDING_CONCURRENCY,
        worker: async (item) => {
          try {
            const understanding = await trpc.feishuSheets.understandImage.mutate({
              fileToken: item.fileToken,
              spreadsheetToken: result.spreadsheetToken,
              width: item.width,
              height: item.height,
            });
            if (!understanding.content?.trim()) {
              return {
                ok: false as const,
                item,
                error: "图片理解结果为空",
              };
            }
            return { ok: true as const, item, understanding };
          } catch (error) {
            return {
              ok: false as const,
              item,
              error: error instanceof Error ? error.message : "图片理解失败",
            };
          }
        },
        onItemComplete: ({ item, done, result: taskResult }) => {
          if (taskResult.ok) {
            setImageUnderstandings((prev) => ({
              ...prev,
              [item.fileToken]: {
                content: taskResult.understanding.content,
                cached: taskResult.understanding.cached,
                understoodAt: taskResult.understanding.understoodAt,
              },
            }));
          }

          setImageProgress((prev) => ({
            ...prev,
            done,
            success: taskResult.ok ? prev.success + 1 : prev.success,
            fail: taskResult.ok ? prev.fail : prev.fail + 1,
            currentLabel: `第 ${item.rowNumber} 行 · ${item.column} 列`,
            failures: taskResult.ok
              ? prev.failures
              : [
                  ...prev.failures,
                  {
                    fileToken: item.fileToken,
                    label: `第 ${item.rowNumber} 行 · ${item.column} 列`,
                    error: taskResult.error,
                  },
                ],
          }));
        },
      });
      message.success("图片理解完成");
    } finally {
      setImageProgress((prev) => ({ ...prev, running: false }));
    }
  };

  const assignmentCacheLookup = useMemo(() => {
    if (!result || !selectedGroup) return null;
    return buildScriptAssignmentCacheLookup({
      spreadsheetToken: result.spreadsheetToken,
      sheetId: result.sheetId,
      group: selectedGroup,
      parsedRows: allParsedRows,
      overwriteMode,
    });
  }, [allParsedRows, result, selectedGroup, overwriteMode]);

  useEffect(() => {
    if (!assignmentCacheLookup) {
      setServerAssignmentCache(null);
      return;
    }

    let cancelled = false;
    void trpc.feishuSheets.getRoleAssignmentCache
      .query(assignmentCacheLookup)
      .then((cache) => {
        if (!cancelled) {
          setServerAssignmentCache(cache);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setServerAssignmentCache(null);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [assignmentCacheLookup]);

  const assignmentResumeState = useMemo(() => {
    const cache = serverAssignmentCache;
    if (!cache?.tempRoles.length) return null;
    const personaCount = Object.keys(cache.personas).length;
    const matchCount = cache.assignments.length;
    const total = cache.tempRoles.length;
    if (personaCount === 0 && matchCount === 0) return null;
    if (matchCount >= total) return null;

    const personasComplete = areAllPersonasComplete(cache.tempRoles, cache.personas);
    return { cache, personasComplete, personaCount, matchCount, total };
  }, [serverAssignmentCache]);

  const resumableAssignment = assignmentResumeState?.cache ?? null;

  const hasAssignmentCache = useMemo(() => {
    if (
      assignmentResult &&
      (assignmentResult.assignments.length > 0 || assignmentResult.personas.length > 0)
    ) {
      return true;
    }
    const cache = serverAssignmentCache;
    if (!cache?.tempRoles.length) return false;
    return (
      Object.keys(cache.personas).length > 0 ||
      cache.assignments.length > 0 ||
      Boolean(cache.matchJob)
    );
  }, [serverAssignmentCache, assignmentResult]);

  const resetAssignmentLocalState = useCallback(() => {
    assignmentRunActiveRef.current = false;
    completedCacheRestoredRef.current = null;
    setServerAssignmentCache(null);
    setAssignmentResult(null);
    setAssignmentProgress(INITIAL_ASSIGNMENT_PROGRESS);
    setAssigning(false);
    setReviewOpen(false);
    setReviewWriting(false);
    setReviewReceipt(null);
    autoOpenedReviewKeyRef.current = null;
    setShowAssignedPreview(false);
    setRetryingPersonaTempRoleId(null);
    setPersonaRetryRunning(false);
  }, []);

  const primaryBlockDiagnostics = useMemo(
    () => assignmentProgress.items.find((item) => item.blockDiagnostics)?.blockDiagnostics,
    [assignmentProgress.items]
  );

  const assignmentOverallStatus = useMemo(
    () => resolveAssignmentOverallStatus(assignmentProgress, assigning),
    [assignmentProgress, assigning]
  );

  const assignmentPersonaErrorCount = useMemo(
    () => countRetryablePersonaFailures(assignmentProgress.items),
    [assignmentProgress.items]
  );

  const assignmentPersonaProgressStatus = useMemo(() => {
    if (assignmentProgress.running && assignmentProgress.phase === "persona") return "active";
    if (countAssignmentItemsByStatus(assignmentProgress.items, "error") > 0) return "exception";
    if (
      assignmentProgress.total > 0 &&
      assignmentProgress.personaDone >= assignmentProgress.total
    ) {
      return "success";
    }
    return "normal";
  }, [assignmentProgress]);

  const assignmentMatchProgressStatus = useMemo(() => {
    if (
      (assignmentProgress.running || assigning) &&
      assignmentProgress.phase === "matching"
    ) {
      return "active";
    }
    if (
      assignmentProgress.matchDone < assignmentProgress.total &&
      countAssignmentItemsByStatus(assignmentProgress.items, "error") > 0
    ) {
      return "exception";
    }
    if (
      assignmentProgress.total > 0 &&
      assignmentProgress.matchDone >= assignmentProgress.total
    ) {
      return "success";
    }
    return "normal";
  }, [assignmentProgress, assigning]);

  useEffect(() => {
    const isMatchingLive =
      (assignmentProgress.running && assignmentProgress.phase === "matching") || assigning;
    if (!isMatchingLive) return;

    const timer = window.setInterval(() => {
      setAssignmentNowTick(Date.now());
    }, 1000);
    return () => window.clearInterval(timer);
  }, [
    assigning,
    assignmentProgress.running,
    assignmentProgress.phase,
    assignmentProgress.activeTempRoleId,
    assignmentProgress.activeSince,
    assignmentProgress.matchStartedAt,
  ]);

  const assignmentCurrentTaskLabel = useMemo(() => {
    const label = assignmentProgress.currentLabel;
    if (assignmentProgress.phase === "matching" && (assignmentProgress.running || assigning)) {
      const since = assignmentProgress.activeSince ?? assignmentProgress.matchStartedAt;
      if (since) {
        const elapsed = formatElapsedSeconds(since, assignmentNowTick);
        const baseLabel = label.replace(/（已耗时 \d+ 秒）$/, "");
        return `${baseLabel}（已耗时 ${elapsed} 秒）`;
      }
    }
    return label;
  }, [assignmentProgress, assignmentNowTick, assigning]);

  useEffect(() => {
    if (!selectedGroup || !assignmentResult) return;
    if (assignmentResult.group !== selectedGroup) {
      setAssignmentResult(null);
      setShowAssignedPreview(false);
    }
  }, [selectedGroup, assignmentResult?.group]);

  useEffect(() => {
    if (!resumableAssignment) return;
    const validAssignments = resumableAssignment.assignments.filter(
      (item) => Array.isArray(item.scoreBreakdown)
    );
    setAssignmentResult({
      spreadsheetToken: resumableAssignment.spreadsheetToken,
      sheetId: resumableAssignment.sheetId,
      group: resumableAssignment.group,
      overwriteMode,
      imageTextsByFileToken: buildImageTextsPayload(),
      tempRoles: resumableAssignment.tempRoles,
      personas: Object.values(resumableAssignment.personas),
      assignments: validAssignments,
      previewRows: parsedRows.map((row) => ({ ...row, assignedRole: row.role })),
      replacements: [],
    });
  }, [resumableAssignment, parsedRows]);

  useEffect(() => {
    if (!serverAssignmentCache || !parsedRows.length) return;
    const { tempRoles, assignments } = serverAssignmentCache;
    if (!tempRoles.length || assignments.length < tempRoles.length) return;

    const validAssignments = assignments.filter((item) => Array.isArray(item.scoreBreakdown));
    if (validAssignments.length < tempRoles.length) return;

    const cacheId = `${serverAssignmentCache.spreadsheetToken}:${serverAssignmentCache.sheetId}:${serverAssignmentCache.group}:${serverAssignmentCache.updatedAt}`;
    if (completedCacheRestoredRef.current === cacheId) return;
    completedCacheRestoredRef.current = cacheId;

    let cancelled = false;
    void (async () => {
      try {
        const finalized = await trpc.feishuSheets.finalizeRoleAssignment.mutate({
          spreadsheetToken: serverAssignmentCache.spreadsheetToken,
          sheetId: serverAssignmentCache.sheetId,
          sourceFingerprint: serverAssignmentCache.sourceFingerprint,
          group: serverAssignmentCache.group,
          parsedRows: allParsedRows,
          imageTextsByFileToken: serverAssignmentCache.imageTextsByFileToken,
          personas: Object.values(serverAssignmentCache.personas),
          assignments: validAssignments,
          overwriteMode,
        });
        if (!cancelled) {
          setAssignmentResult(finalized);
          setShowAssignedPreview(true);
        }
      } catch (error: unknown) {
        if (!cancelled) {
          completedCacheRestoredRef.current = null;
          setAssignmentResult(null);
          message.error(
            error instanceof Error
              ? `恢复匹配结果失败：${error.message}`
              : "恢复匹配结果失败，请重新匹配"
          );
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [allParsedRows, message, serverAssignmentCache, overwriteMode]);

  const buildImageTextsPayload = () => {
    const payload: Record<string, string> = {};
    for (const [fileToken, item] of Object.entries(imageUnderstandings)) {
      if (item.content?.trim()) {
        payload[fileToken] = item.content.trim();
      }
    }
    return payload;
  };

  const runRoleAssignment = async (options: {
    resume: boolean;
    skipPersona?: boolean;
    stopAfterPersona?: boolean;
  }) => {
    if (assignmentRunActiveRef.current) {
      tryReopenActiveAssignmentProgress();
      return;
    }

    if (!result) {
      message.warning("请先读取飞书表格");
      return;
    }
    if (!selectedGroup) {
      message.warning("请选择气氛员分组");
      return;
    }
    if (!parsedRows.length) {
      message.warning("当前没有可分配的气氛员剧本行");
      return;
    }

    const cacheLookup = buildScriptAssignmentCacheLookup({
      spreadsheetToken: result.spreadsheetToken,
      sheetId: result.sheetId,
      group: selectedGroup,
      parsedRows: allParsedRows,
      overwriteMode,
    });

    const existingCache =
      serverAssignmentCache ??
      (await trpc.feishuSheets.getRoleAssignmentCache.query(cacheLookup));

    assignmentRunActiveRef.current = true;
    setAssigning(true);
    if (!options.resume && !options.skipPersona) {
      setReviewOpen(false);
      setReviewReceipt(null);
      autoOpenedReviewKeyRef.current = null;
    }
    if (
      !options.resume &&
      !options.skipPersona &&
      Object.keys(existingCache?.personas ?? {}).length === 0
    ) {
      setAssignmentResult(null);
      setShowAssignedPreview(false);
    }
    if (options.skipPersona) {
      setAssignmentProgress((prev) => ({
        ...prev,
        open: true,
        running: true,
        phase: "matching",
        currentLabel: "按剧本顺序匹配真实气氛员…",
        tokenUsage: prev.open ? prev.tokenUsage : buildInitialAssignmentTokenUsage(),
      }));
    } else {
      setAssignmentProgress({
        ...INITIAL_ASSIGNMENT_PROGRESS,
        open: true,
        phase: "prepare",
        running: true,
        currentLabel: options.resume ? "恢复画像进度…" : "准备分配数据…",
        tokenUsage: buildInitialAssignmentTokenUsage(),
      });
    }

    let imageTextsByFileToken = {
      ...buildImageTextsPayload(),
      ...(existingCache?.imageTextsByFileToken ?? {}),
    };
    const personaMap: Record<string, ScriptRolePersona> = {
      ...(existingCache?.personas ?? {}),
    };
    // 进度 UI / 本地结果可能已齐，但并发写缓存曾把服务端 personas 冲缺；用本地已有画像补齐后再落盘
    for (const item of assignmentProgress.items) {
      if (item.persona && !personaMap[item.tempRoleId]) {
        personaMap[item.tempRoleId] = item.persona;
      }
    }
    for (const persona of assignmentResult?.personas ?? []) {
      if (persona.tempRoleId && !personaMap[persona.tempRoleId]) {
        personaMap[persona.tempRoleId] = persona;
      }
    }
    const assignments: RoleAssignmentItem[] = [...(existingCache?.assignments ?? [])];
    const assignmentIds = new Set(assignments.map((item) => item.tempRoleId));
    let tokenUsageState = buildInitialAssignmentTokenUsage();

    const writeCache = async (input: {
      tempRoles: ScriptAssignmentTempRole[];
      memberCount: number;
    }) => {
      const saved = await persistAssignmentCache(cacheLookup, {
        spreadsheetToken: result.spreadsheetToken,
        sheetId: result.sheetId,
        group: selectedGroup,
        sourceFingerprint: cacheLookup.sourceFingerprint,
        imageTextsByFileToken,
        tempRoles: input.tempRoles,
        memberCount: input.memberCount,
        personas: personaMap,
        assignments,
        updatedAt: new Date().toISOString(),
      });
      setServerAssignmentCache(saved);
    };

    try {
      const prepared = await trpc.feishuSheets.prepareRoleAssignment.mutate({
        spreadsheetToken: result.spreadsheetToken,
        sheetId: result.sheetId,
        sourceFingerprint: cacheLookup.sourceFingerprint,
        group: selectedGroup,
        parsedRows: allParsedRows,
        imageTextsByFileToken,
        overwriteMode,
      });

      imageTextsByFileToken = prepared.imageTextsByFileToken;
      for (const [tempRoleId, persona] of Object.entries(prepared.inlinePersonas ?? {})) {
        if (!personaMap[tempRoleId]) {
          personaMap[tempRoleId] = persona;
        }
      }

      const progressItems = buildAssignmentProgressItems(prepared.tempRoles, {
        spreadsheetToken: result.spreadsheetToken,
        sheetId: result.sheetId,
        group: selectedGroup,
        sourceFingerprint: cacheLookup.sourceFingerprint,
        imageTextsByFileToken,
        tempRoles: prepared.tempRoles,
        memberCount: prepared.memberCount,
        personas: personaMap,
        assignments,
        updatedAt: new Date().toISOString(),
      });

      const llmPersonaPending = prepared.tempRoles.filter(
        (role) => !personaMap[role.tempRoleId] && !shouldUseInlinePersona(role.utteranceCount)
      ).length;

      setAssignmentProgress((prev) => ({
        ...prev,
        phase: "persona",
        memberCount: prepared.memberCount,
        total: progressItems.length,
        personaDone: countCompletedPersonas(prepared.tempRoles, personaMap),
        matchDone: assignments.length,
        items: progressItems,
        currentLabel:
          llmPersonaPending > 0
            ? "分析多句角色画像…"
            : countCompletedPersonas(prepared.tempRoles, personaMap) > 0
              ? "单句角色已使用原话，准备匹配…"
              : "准备匹配…",
      }));

      if (assignments.length > 0 || countCompletedPersonas(prepared.tempRoles, personaMap) > 0) {
        setAssignmentResult({
          spreadsheetToken: result.spreadsheetToken,
          sheetId: result.sheetId,
          group: selectedGroup,
          overwriteMode,
          imageTextsByFileToken,
          tempRoles: prepared.tempRoles,
          personas: Object.values(personaMap),
          assignments,
          previewRows: parsedRows.map((row) => ({ ...row, assignedRole: row.role })),
          replacements: [],
        });
      }

      await writeCache({
        tempRoles: prepared.tempRoles,
        memberCount: prepared.memberCount,
      });

      if (!options.skipPersona) {
        const rolesNeedingPersona = prepared.tempRoles.filter(
          (role) => !personaMap[role.tempRoleId] && !shouldUseInlinePersona(role.utteranceCount)
        );

        if (rolesNeedingPersona.length > 0) {
          const personaResults = await runConcurrentTasks({
            items: rolesNeedingPersona,
            concurrency: SCRIPT_ROLE_ASSIGNMENT_CONCURRENCY,
            onItemStart: ({ item }) => {
              setAssignmentProgress((prev) => ({
                ...prev,
                activeTempRoleId: item.tempRoleId,
                activeSince: null,
                currentLabel: `画像分析中：气氛员${item.tempRoleId}`,
                items: prev.items.map((row) =>
                  row.tempRoleId === item.tempRoleId ? { ...row, status: "persona" } : row
                ),
              }));
            },
            worker: async (role) => {
              try {
                const personaResult = await trpc.feishuSheets.generateRolePersona.mutate({
                  spreadsheetToken: result.spreadsheetToken,
                  sheetId: result.sheetId,
                  group: selectedGroup,
                  sourceFingerprint: cacheLookup.sourceFingerprint,
                  parsedRows: allParsedRows,
                  imageTextsByFileToken,
                  tempRoleId: role.tempRoleId,
                  overwriteMode,
                });
                return { ok: true as const, role, personaResult };
              } catch (error) {
                return {
                  ok: false as const,
                  role,
                  error: error instanceof Error ? error.message : "画像分析失败",
                };
              }
            },
            onItemComplete: ({ item, result: taskResult }) => {
              if (taskResult.ok) {
                personaMap[taskResult.personaResult.persona.tempRoleId] =
                  taskResult.personaResult.persona;
                tokenUsageState = mergeAssignmentTokenUsage(
                  tokenUsageState,
                  "persona",
                  taskResult.personaResult.tokenUsage
                );
              }

              setAssignmentProgress((prev) => ({
                ...prev,
                personaDone: countCompletedPersonas(prepared.tempRoles, personaMap),
                tokenUsage: tokenUsageState,
                items: prev.items.map((row) => {
                  if (row.tempRoleId !== item.tempRoleId) return row;
                  if (taskResult.ok) {
                    return {
                      ...row,
                      status: "persona_ready",
                      persona: taskResult.personaResult.persona,
                      error: undefined,
                    };
                  }
                  return { ...row, status: "error", error: taskResult.error };
                }),
                currentLabel: taskResult.ok
                  ? `画像完成：气氛员${item.tempRoleId}`
                  : `画像失败：气氛员${item.tempRoleId}`,
              }));
            },
          });

          const personaFailures = personaResults.filter((item) => !item.ok);
          // 并发画像结束后再统一落盘，避免过程中 void writeCache 互相覆盖导致服务端缺画像
          await writeCache({
            tempRoles: prepared.tempRoles,
            memberCount: prepared.memberCount,
          });
          if (personaFailures.length > 0) {
            setAssignmentProgress((prev) => ({
              ...prev,
              open: true,
              phase: "persona",
              running: false,
              activeTempRoleId: null,
              activeSince: null,
              personaDone: countCompletedPersonas(prepared.tempRoles, personaMap),
              currentLabel: "画像分析失败，请在下方重试失败项",
            }));
            message.error(
              `${personaFailures.length} 个画像分析失败，请在进度窗口中重试失败项`
            );
            return;
          }
        } else {
          await writeCache({
            tempRoles: prepared.tempRoles,
            memberCount: prepared.memberCount,
          });
        }

        if (!areAllPersonasComplete(prepared.tempRoles, personaMap)) {
          await writeCache({
            tempRoles: prepared.tempRoles,
            memberCount: prepared.memberCount,
          });
          setAssignmentProgress((prev) => ({
            ...prev,
            open: true,
            phase: "persona",
            running: false,
            activeTempRoleId: null,
            activeSince: null,
            personaDone: countCompletedPersonas(prepared.tempRoles, personaMap),
            currentLabel: "画像未完成，请在下方重试失败项",
          }));
          message.warning(`仍有角色缺少${SCRIPT_ROLE_PERSONA_TERM}，请在进度窗口中重试后再匹配`);
          return;
        }

        if (options.stopAfterPersona) {
          await writeCache({
            tempRoles: prepared.tempRoles,
            memberCount: prepared.memberCount,
          });
          setAssignmentProgress((prev) => ({
            ...prev,
            phase: "persona",
            running: false,
            activeTempRoleId: null,
            activeSince: null,
            personaDone: countCompletedPersonas(prepared.tempRoles, personaMap),
            currentLabel: "画像已完成，请点击「开始匹配」",
          }));
          message.success("画像分析完成，请确认后开始匹配气氛员");
          return;
        }
      } else if (!areAllPersonasComplete(prepared.tempRoles, personaMap)) {
        setAssignmentProgress((prev) => ({
          ...prev,
          running: false,
          currentLabel: "画像未完成，无法开始匹配",
        }));
        message.warning(`请先完成全部${SCRIPT_ROLE_PERSONA_TERM}分析后再匹配`);
        return;
      } else if (options.skipPersona) {
        const progressItems = buildAssignmentProgressItems(prepared.tempRoles, {
          spreadsheetToken: result.spreadsheetToken,
          sheetId: result.sheetId,
          group: selectedGroup,
          sourceFingerprint: cacheLookup.sourceFingerprint,
          imageTextsByFileToken,
          tempRoles: prepared.tempRoles,
          memberCount: prepared.memberCount,
          personas: personaMap,
          assignments,
          updatedAt: new Date().toISOString(),
        });
        // 开始匹配前把前端已齐的画像强制写回服务端，避免旧并发写覆盖后的缓存缺项
        await writeCache({
          tempRoles: prepared.tempRoles,
          memberCount: prepared.memberCount,
        });
        setAssignmentProgress((prev) => ({
          ...prev,
          memberCount: prepared.memberCount,
          total: progressItems.length,
          personaDone: countCompletedPersonas(prepared.tempRoles, personaMap),
          matchDone: assignments.length,
          items: progressItems.length > 0 ? progressItems : prev.items,
        }));
      }

      const rolesNeedingMatch = prepared.tempRoles.filter(
        (role) => !assignmentIds.has(role.tempRoleId) && personaMap[role.tempRoleId]
      );

      if (rolesNeedingMatch.length > 0) {
        // 无论是否刚跑完画像，匹配前再落盘一次，保证 startBatchMatch 读到的缓存完整
        await writeCache({
          tempRoles: prepared.tempRoles,
          memberCount: prepared.memberCount,
        });
        const matchStartedAt = Date.now();
        const rolesNeedingMatchIds = new Set(rolesNeedingMatch.map((role) => role.tempRoleId));
        const existingMatchJob = existingCache?.matchJob;
        const isMatchRetry = Boolean(
          existingMatchJob && isResumableScriptRoleMatchJob(existingMatchJob)
        );
        setAssignmentNowTick(matchStartedAt);
        setAssignmentProgress((prev) => ({
          ...prev,
          open: true,
          running: true,
          phase: "matching",
          personaDone: countCompletedPersonas(prepared.tempRoles, personaMap),
          matchStartedAt,
          matchJobStatus: isMatchRetry
            ? existingMatchJob?.status === "failed"
              ? "scoring"
              : existingMatchJob?.status ?? "scoring"
            : "scoring",
          matchScoringDone: isMatchRetry ? (existingMatchJob?.scoringDone ?? 0) : 0,
          matchSelectionDone: isMatchRetry ? (existingMatchJob?.selectionDone ?? 0) : 0,
          matchJobTotal: rolesNeedingMatch.length,
          currentLabel: isMatchRetry
            ? `续跑匹配…（已完成 ${assignments.length}/${prepared.tempRoles.length}）`
            : `正在启动匹配…（0/${rolesNeedingMatch.length}）`,
          items: prev.items.map((row) =>
            rolesNeedingMatchIds.has(row.tempRoleId) && row.status !== "done"
              ? { ...row, status: "matching" as const, error: undefined }
              : row
          ),
        }));

        let startError: string | null = null;
        setAssignmentProgress((prev) => ({
          ...prev,
          matchPoll: {
            ...prev.matchPoll,
            startStatus: "pending",
            startError: null,
          },
        }));

        try {
          await trpc.feishuSheets.startBatchMatchRoleAssignments.mutate({
            spreadsheetToken: result.spreadsheetToken,
            sheetId: result.sheetId,
            group: selectedGroup,
            sourceFingerprint: cacheLookup.sourceFingerprint,
            parsedRows: allParsedRows,
            overwriteMode,
          });
          setAssignmentProgress((prev) => ({
            ...prev,
            matchPoll: {
              ...prev.matchPoll,
              startStatus: "accepted",
              startError: null,
            },
          }));
        } catch (error: any) {
          startError = error?.message || "启动批量匹配失败";
          setAssignmentProgress((prev) => ({
            ...prev,
            matchPoll: {
              ...prev.matchPoll,
              startStatus: "failed",
              startError: startError,
            },
            phase: "done",
            running: false,
            currentLabel: startError ?? "启动批量匹配失败",
          }));
          message.error(startError);
          return;
        }

        let finalCache: ScriptAssignmentSessionCache;
        try {
          finalCache = await waitForMatchProgressViaPolling({
            cacheLookup,
            tempRoles: prepared.tempRoles,
            targetAssignmentCount: prepared.tempRoles.length,
            getEarlyError: () => startError,
            setAssignmentProgress,
            setAssignmentResult,
            result,
            selectedGroup,
            parsedRows,
            overwriteMode,
            imageTextsByFileToken,
          });
        } catch (error: any) {
          setAssignmentProgress((prev) => ({
            ...prev,
            items: prev.items.map((row) => {
              const needsMatch = rolesNeedingMatch.some(
                (r) => r.tempRoleId === row.tempRoleId
              );
              if (needsMatch && row.status !== "done") {
                return { ...row, status: "error" as const, error: error?.message || "批量匹配失败" };
              }
              return row;
            }),
            phase: "done",
            running: false,
            activeTempRoleId: null,
            activeSince: null,
            matchDone: assignments.length,
            currentLabel: "批量匹配出错",
          }));
          message.error(error?.message || "批量匹配失败");
          return;
        }

        const matchJob = finalCache.matchJob;
        const matchFailures = matchJob?.failures ?? [];

        for (const assignment of finalCache.assignments) {
          if (!assignmentIds.has(assignment.tempRoleId)) {
            assignments.push(assignment);
            assignmentIds.add(assignment.tempRoleId);
          }
        }
        if (matchJob?.matchTokenUsage) {
          tokenUsageState = mergeAssignmentTokenUsage(
            tokenUsageState,
            "match",
            matchJob.matchTokenUsage
          );
        }

        void writeCache({
          tempRoles: prepared.tempRoles,
          memberCount: prepared.memberCount,
        });

        setAssignmentProgress((prev) => ({
          ...prev,
          matchDone: assignments.length,
          tokenUsage: tokenUsageState,
          items: prev.items.map((row) => {
            const match = assignments.find((a) => a.tempRoleId === row.tempRoleId);
            if (match) {
              return { ...row, status: "done" as const, assignment: match, error: undefined };
            }
            const fail = matchFailures.find((f) => f.tempRoleId === row.tempRoleId);
            if (fail) {
              return {
                ...row,
                status: fail.skipped ? ("skipped" as const) : ("error" as const),
                error: fail.error,
                blockDiagnostics: fail.blockDiagnostics,
              };
            }
            return row;
          }),
          currentLabel:
            matchFailures.length > 0
              ? `批量匹配完成，${matchFailures.length} 项失败`
              : `批量匹配完成（${assignments.length}/${prepared.tempRoles.length}）`,
        }));

        setAssignmentResult((prev) => ({
          spreadsheetToken: result.spreadsheetToken,
          sheetId: result.sheetId,
          group: selectedGroup,
          overwriteMode,
          imageTextsByFileToken,
          tempRoles: prepared.tempRoles,
          personas: Object.values(personaMap),
          assignments: [...assignments],
          previewRows:
            prev?.previewRows ??
            parsedRows.map((row) => ({ ...row, assignedRole: row.role })),
          replacements: prev?.replacements ?? [],
        }));

        if (matchJob?.status === "failed") {
          setAssignmentProgress((prev) => ({
            ...prev,
            phase: "done",
            running: false,
            activeTempRoleId: null,
            activeSince: null,
          }));
          message.error(matchJob.error ?? "批量匹配失败");
          return;
        }

        if (matchFailures.length > 0) {
          setAssignmentProgress((prev) => ({
            ...prev,
            phase: "done",
            running: false,
            activeTempRoleId: null,
            activeSince: null,
          }));
          message.warning(
            `匹配完成 ${assignments.length}/${prepared.tempRoles.length}，${matchFailures.length} 项失败，可继续匹配重试`
          );
          return;
        }
      } else if (!areAllPersonasComplete(prepared.tempRoles, personaMap)) {
        await writeCache({
          tempRoles: prepared.tempRoles,
          memberCount: prepared.memberCount,
        });
        setAssignmentProgress((prev) => ({
          ...prev,
          open: true,
          phase: "persona",
          running: false,
          activeTempRoleId: null,
          activeSince: null,
          currentLabel: "画像未完成，请在下方重试失败项",
        }));
        message.warning(
          `仅完成 ${countCompletedPersonas(prepared.tempRoles, personaMap)}/${prepared.tempRoles.length} 个画像，请在进度窗口中重试`
        );
        return;
      }

      if (assignments.length < prepared.tempRoles.length) {
        await writeCache({
          tempRoles: prepared.tempRoles,
          memberCount: prepared.memberCount,
        });
        setAssignmentProgress((prev) => ({
          ...prev,
          open: true,
          phase: "done",
          running: false,
          activeTempRoleId: null,
          activeSince: null,
          currentLabel: "部分完成，可继续匹配",
        }));
        message.warning("仍有角色未完成匹配，可点击「继续匹配」继续");
        return;
      }

      const finalized = await trpc.feishuSheets.finalizeRoleAssignment.mutate({
        spreadsheetToken: result.spreadsheetToken,
        sheetId: result.sheetId,
        sourceFingerprint: cacheLookup.sourceFingerprint,
        group: selectedGroup,
        parsedRows: allParsedRows,
        imageTextsByFileToken,
        personas: Object.values(personaMap),
        assignments,
        overwriteMode,
      });

      const saved = await persistAssignmentCache(cacheLookup, {
        spreadsheetToken: result.spreadsheetToken,
        sheetId: result.sheetId,
        group: selectedGroup,
        sourceFingerprint: cacheLookup.sourceFingerprint,
        imageTextsByFileToken,
        tempRoles: finalized.tempRoles,
        memberCount: prepared.memberCount,
        personas: Object.fromEntries(
          finalized.personas.map((persona) => [persona.tempRoleId, persona])
        ),
        assignments: finalized.assignments,
        updatedAt: new Date().toISOString(),
      });
      setServerAssignmentCache(saved);
      setAssignmentResult(finalized);
      setShowAssignedPreview(true);
      setAssignmentProgress((prev) => ({
        ...prev,
        phase: "done",
        running: false,
        activeTempRoleId: null,
        activeSince: null,
        matchDone: assignments.length,
        currentLabel: `全部分配完成（${assignments.length}/${prepared.tempRoles.length}）`,
      }));
      message.success("气氛员分配完成");
    } catch (error: any) {
      setAssignmentProgress((prev) => ({
        ...prev,
        open: true,
        phase: "done",
        running: false,
      }));
      message.error(error?.message || "气氛员分配失败");
    } finally {
      assignmentRunActiveRef.current = false;
      setAssigning(false);
    }
  };

  const handleAssignRoles = () => {
    if (tryReopenActiveAssignmentProgress()) return;
    const hasCachedPersonas = Object.keys(serverAssignmentCache?.personas ?? {}).length > 0;
    void runRoleAssignment({ resume: hasCachedPersonas });
  };

  const handleResumeAssignRoles = () => {
    if (tryReopenActiveAssignmentProgress()) return;
    const personaErrorCount = countRetryablePersonaFailures(assignmentProgress.items);
    if (personaErrorCount > 0) {
      openAssignmentProgressModal();
      return;
    }
    if (assignmentProgress.total > 0 && !assignmentProgress.open) {
      openAssignmentProgressModal();
      return;
    }
    void runRoleAssignment({ resume: true, stopAfterPersona: true });
  };

  const handleStartMatching = () => {
    if (tryReopenActiveAssignmentProgress()) return;
    void runRoleAssignment({ resume: true, skipPersona: true });
  };

  const handleRetryPersona = async (tempRoleId: string) => {
    if (!result || !selectedGroup || !assignmentCacheLookup) {
      message.warning("请先读取飞书表格并选择分组");
      return;
    }

    const roleMeta =
      serverAssignmentCache?.tempRoles.find((item) => item.tempRoleId === tempRoleId) ??
      assignmentProgress.items.find((item) => item.tempRoleId === tempRoleId);
    if (!roleMeta) {
      message.error(`找不到临时角色 ${tempRoleId}`);
      return;
    }
    if (shouldUseInlinePersona(roleMeta.utteranceCount)) {
      message.info("单句角色使用原话画像，无需重试");
      return;
    }

    const imageTextsByFileToken = {
      ...buildImageTextsPayload(),
      ...(serverAssignmentCache?.imageTextsByFileToken ?? {}),
    };

    setPersonaRetryRunning(true);
    setRetryingPersonaTempRoleId(tempRoleId);
    setAssignmentProgress((prev) => ({
      ...prev,
      open: true,
      phase: "persona",
      running: true,
      activeTempRoleId: tempRoleId,
      items: prev.items.map((row) =>
        row.tempRoleId === tempRoleId
          ? { ...row, status: "persona", error: undefined }
          : row
      ),
      currentLabel: `重试画像中：气氛员${tempRoleId}`,
    }));

    try {
      const personaResult = await trpc.feishuSheets.generateRolePersona.mutate({
        spreadsheetToken: result.spreadsheetToken,
        sheetId: result.sheetId,
        group: selectedGroup,
        sourceFingerprint: assignmentCacheLookup.sourceFingerprint,
        parsedRows: allParsedRows,
        imageTextsByFileToken,
        tempRoleId,
        overwriteMode,
      });
      const persona = personaResult.persona;
      const baseCache =
        serverAssignmentCache ??
        (await trpc.feishuSheets.getRoleAssignmentCache.query(assignmentCacheLookup));
      if (!baseCache) {
        message.error("分配缓存不存在，请重新开始分配");
        return;
      }

      const nextPersonas = { ...baseCache.personas, [tempRoleId]: persona };
      const saved = await persistAssignmentCache(assignmentCacheLookup, {
        ...baseCache,
        personas: nextPersonas,
        updatedAt: new Date().toISOString(),
      });
      setServerAssignmentCache(saved);

      const personasComplete = areAllPersonasComplete(baseCache.tempRoles, nextPersonas);
      setAssignmentProgress((prev) => ({
        ...prev,
        running: false,
        activeTempRoleId: null,
        activeSince: null,
        personaDone: Object.keys(nextPersonas).length,
        items: prev.items.map((row) =>
          row.tempRoleId === tempRoleId
            ? { ...row, status: "persona_ready", persona, error: undefined }
            : row
        ),
        currentLabel: personasComplete
          ? "画像已完成，请点击「开始匹配」"
          : `画像完成：气氛员${tempRoleId}`,
      }));

      setAssignmentResult((prev) =>
        prev
          ? {
              ...prev,
              personas: Object.values(nextPersonas),
            }
          : {
              spreadsheetToken: result.spreadsheetToken,
              sheetId: result.sheetId,
              group: selectedGroup,
              overwriteMode,
              imageTextsByFileToken: buildImageTextsPayload(),
              tempRoles: baseCache.tempRoles,
              personas: Object.values(nextPersonas),
              assignments: baseCache.assignments,
              previewRows: parsedRows.map((row) => ({ ...row, assignedRole: row.role })),
              replacements: [],
            }
      );

      message.success(
        personasComplete
          ? `气氛员${tempRoleId} 画像已更新，全部画像已完成，可以开始匹配`
          : `气氛员${tempRoleId} 画像已更新`
      );
    } catch (error: any) {
      setAssignmentProgress((prev) => ({
        ...prev,
        running: false,
        activeTempRoleId: null,
        activeSince: null,
        items: prev.items.map((row) =>
          row.tempRoleId === tempRoleId
            ? {
                ...row,
                status: "error",
                error: error?.message || "画像分析失败",
              }
            : row
        ),
        currentLabel: `画像失败：气氛员${tempRoleId}，可继续重试`,
      }));
      message.error(error?.message || "画像重试失败");
    } finally {
      setRetryingPersonaTempRoleId(null);
      setPersonaRetryRunning(false);
    }
  };

  const handleRetryAllFailedPersonas = async () => {
    if (!result || !selectedGroup || !assignmentCacheLookup) {
      message.warning("请先读取飞书表格并选择分组");
      return;
    }

    const failedRoles = assignmentProgress.items.filter(
      (item) => item.status === "error" && !shouldUseInlinePersona(item.utteranceCount)
    );
    if (!failedRoles.length) {
      message.info("没有需要重试的画像");
      return;
    }

    const imageTextsByFileToken = {
      ...buildImageTextsPayload(),
      ...(serverAssignmentCache?.imageTextsByFileToken ?? {}),
    };

    setPersonaRetryRunning(true);
    setAssignmentProgress((prev) => ({
      ...prev,
      open: true,
      phase: "persona",
      running: true,
      activeTempRoleId: null,
      currentLabel: `重试 ${failedRoles.length} 个失败画像…`,
      items: prev.items.map((row) =>
        failedRoles.some((role) => role.tempRoleId === row.tempRoleId)
          ? { ...row, status: "persona", error: undefined }
          : row
      ),
    }));

    let personaMap: Record<string, ScriptRolePersona> = {
      ...(serverAssignmentCache?.personas ?? {}),
    };

    try {
      const retryResults = await runConcurrentTasks({
        items: failedRoles,
        concurrency: SCRIPT_ROLE_ASSIGNMENT_CONCURRENCY,
        onItemStart: ({ item }) => {
          setAssignmentProgress((prev) => ({
            ...prev,
            activeTempRoleId: item.tempRoleId,
            currentLabel: `重试画像中：气氛员${item.tempRoleId}`,
          }));
        },
        worker: async (role) => {
          try {
            const personaResult = await trpc.feishuSheets.generateRolePersona.mutate({
              spreadsheetToken: result.spreadsheetToken,
              sheetId: result.sheetId,
              group: selectedGroup,
              sourceFingerprint: assignmentCacheLookup.sourceFingerprint,
              parsedRows: allParsedRows,
              imageTextsByFileToken,
              tempRoleId: role.tempRoleId,
              overwriteMode,
            });
            return { ok: true as const, role, personaResult };
          } catch (error) {
            return {
              ok: false as const,
              role,
              error: error instanceof Error ? error.message : "画像分析失败",
            };
          }
        },
        onItemComplete: ({ item, result: taskResult }) => {
          if (taskResult.ok) {
            personaMap[taskResult.personaResult.persona.tempRoleId] =
              taskResult.personaResult.persona;
          }

          setAssignmentProgress((prev) => ({
            ...prev,
            personaDone: Object.keys(personaMap).length,
            items: prev.items.map((row) => {
              if (row.tempRoleId !== item.tempRoleId) return row;
              if (taskResult.ok) {
                return {
                  ...row,
                  status: "persona_ready",
                  persona: taskResult.personaResult.persona,
                  error: undefined,
                };
              }
              return { ...row, status: "error", error: taskResult.error };
            }),
            currentLabel: taskResult.ok
              ? `画像完成：气氛员${item.tempRoleId}`
              : `画像失败：气氛员${item.tempRoleId}`,
          }));
        },
      });

      const baseCache =
        serverAssignmentCache ??
        (await trpc.feishuSheets.getRoleAssignmentCache.query(assignmentCacheLookup));
      if (baseCache) {
        const saved = await persistAssignmentCache(assignmentCacheLookup, {
          ...baseCache,
          personas: personaMap,
          updatedAt: new Date().toISOString(),
        });
        setServerAssignmentCache(saved);
      }

      const tempRoles = baseCache?.tempRoles ?? serverAssignmentCache?.tempRoles ?? [];
      const personasComplete =
        tempRoles.length > 0 && areAllPersonasComplete(tempRoles, personaMap);

      setAssignmentResult((prev) =>
        prev
          ? { ...prev, personas: Object.values(personaMap) }
          : baseCache
            ? {
                spreadsheetToken: result.spreadsheetToken,
                sheetId: result.sheetId,
                group: selectedGroup,
                overwriteMode,
                imageTextsByFileToken: buildImageTextsPayload(),
                tempRoles: baseCache.tempRoles,
                personas: Object.values(personaMap),
                assignments: baseCache.assignments,
                previewRows: parsedRows.map((row) => ({ ...row, assignedRole: row.role })),
                replacements: [],
              }
            : prev
      );

      const finalFailures = retryResults.filter((item) => !item.ok);
      setAssignmentProgress((prev) => ({
        ...prev,
        running: false,
        activeTempRoleId: null,
        activeSince: null,
        personaDone: Object.keys(personaMap).length,
        currentLabel: finalFailures.length
          ? `${finalFailures.length} 个画像仍失败，请继续重试`
          : personasComplete
            ? "画像已完成，请点击「开始匹配」"
            : "画像重试完成",
      }));

      if (finalFailures.length > 0) {
        message.error(`${finalFailures.length} 个画像重试仍失败，请在进度窗口中继续重试`);
      } else if (personasComplete) {
        message.success("全部画像已完成，可以开始匹配");
      } else {
        message.success("失败画像已全部重试完成");
      }
    } finally {
      setPersonaRetryRunning(false);
    }
  };

  const handleClearAssignmentCache = async () => {
    if (!assignmentCacheLookup) return;
    try {
      const cleared = await trpc.feishuSheets.clearRoleAssignmentCache.mutate(assignmentCacheLookup);
      resetAssignmentLocalState();
      const parts: string[] = [];
      if (cleared.deleted) parts.push("服务端缓存");
      if (cleared.queueJobsRemoved > 0) {
        parts.push(`后台匹配任务 ${cleared.queueJobsRemoved} 个`);
      }
      message.success(
        parts.length > 0
          ? `已清除${parts.join("、")}，页面进度已重置`
          : "页面进度已重置（无残留缓存）"
      );
    } catch (error: any) {
      message.error(error?.message || "清除分配缓存失败");
    }
  };

  const handleReviewConfirmed = (result: {
    reviewId: string;
    updatedCells: number;
    changedRoleCount: number;
  }) => {
    setReviewWriting(false);
    setReviewReceipt(result);
    setReviewOpen(false);
    message.success(`已确认并写回飞书，共更新 ${result.updatedCells} 个单元格`);
  };

  const handleReviewConfirmError = () => {
    setReviewWriting(false);
  };

  const complexCellDebug = useMemo(() => {
    if (!result?.rawValues?.length) return [];

    const allRows = parseScriptSheetValues(
      result.values,
      result.rawValues,
      result.roleColumnMerges ?? []
    );
    const visibleRowIndexes = new Set(
      filterScriptSheetRows(allRows).map((row) => row.rowIndex)
    );

    const items: Array<{
      key: string;
      row: number;
      column: string;
      display: string;
      json: string;
    }> = [];

    for (const rowIndex of visibleRowIndexes) {
      const rawRow = result.rawValues[rowIndex] ?? [];
      const formattedRow = result.values[rowIndex] ?? [];

      for (let colIndex = SCRIPT_ROLE_COLUMN_INDEX; colIndex <= SCRIPT_MAX_VISIBLE_COLUMN_INDEX; colIndex += 1) {
        const raw = rawRow[colIndex];
        if (!isFeishuComplexCell(raw)) continue;

        const columnLabel =
          colIndex === SCRIPT_ROLE_COLUMN_INDEX
            ? "B(角色)"
            : columnIndexToLetter(colIndex + 1);

        items.push({
          key: `${rowIndex}-${colIndex}`,
          row: rowIndex + 1,
          column: columnLabel,
          display: formattedRow[colIndex] ?? "",
          json: JSON.stringify(raw, null, 2),
        });
      }
    }

    return items;
  }, [result]);

  const tableColumns = useMemo<ColumnsType<ScriptSheetTableRow>>(() => {
    if (!result?.values.length) return [];

    const dataColumns = getScriptSheetDataColumnLetters();

    const cols: ColumnsType<ScriptSheetTableRow> = [
      {
        title: "#",
        dataIndex: "__row",
        key: "__row",
        width: 64,
        fixed: "left",
      },
      {
        title: showAssignedPreview ? "分配后角色" : "角色",
        dataIndex: "role",
        key: "role",
        width: 140,
        fixed: "left",
        render: (value: string, record) => {
          const displayRole = showAssignedPreview ? record.assignedRole || value : value;
          const imageToken = record.cellImages?.B;
          if (imageToken) {
            return renderScriptSheetCell("", imageToken, imageUnderstandings[imageToken]);
          }
          return (
            <span
              style={{
                color: record.roleIsInherited ? "#64748b" : undefined,
                fontStyle: record.roleIsInherited ? "italic" : undefined,
                fontWeight: showAssignedPreview && displayRole !== value ? 600 : undefined,
              }}
            >
              {displayRole || "—"}
            </span>
          );
        },
      },
      {
        title: "临时角色",
        dataIndex: "tempRoleId",
        key: "tempRoleId",
        width: 96,
        fixed: "left",
        render: (value?: string) => (value ? <Tag>{`气氛员${value}`}</Tag> : "—"),
      },
      {
        title: "B 合并块",
        dataIndex: "blockLabel",
        key: "blockLabel",
        width: 96,
        fixed: "left",
        render: (value?: string) =>
          value ? <Text type="secondary" style={{ fontSize: 12 }}>{value}</Text> : "—",
      },
    ];

    for (const letter of dataColumns) {
      cols.push({
        title: letter === SCRIPT_CONTENT_COLUMN ? "需发内容" : letter,
        dataIndex: letter,
        key: letter,
        width: 180,
        render: (value: string, record) => {
          const imageToken = record.cellImages?.[letter];
          return renderScriptSheetCell(value, imageToken, imageToken ? imageUnderstandings[imageToken] : undefined);
        },
      });
    }

    return cols;
  }, [result, imageUnderstandings, showAssignedPreview]);

  const activePreviewRows = showAssignedPreview && assignmentResult
    ? assignmentResult.previewRows
    : parsedRows;

  const assignmentTableRows = useMemo<AssignmentTableRow[]>(() => {
    if (!assignmentResult) return [];
    const personaByRoleId = Object.fromEntries(
      assignmentResult.personas.map((persona) => [persona.tempRoleId, persona])
    );
    const tempRoleMetaById = Object.fromEntries(
      assignmentResult.tempRoles.map((role) => [role.tempRoleId, role])
    );
    const blockContentRowsById = Object.fromEntries(
      scriptRoleBlocks.map((block) => [block.tempRoleId, block.contentRowNumbers])
    );

    return assignmentResult.assignments
      .filter((assignment) => Array.isArray(assignment.scoreBreakdown))
      .map((assignment) => {
        const contentRowNumbers =
          tempRoleMetaById[assignment.tempRoleId]?.contentRowNumbers ??
          blockContentRowsById[assignment.tempRoleId] ??
          [];
        return {
          ...assignment,
          sampleQuotes: personaByRoleId[assignment.tempRoleId]?.sampleQuotes ?? [],
          contentRowNumbers,
          contentRowRange: formatScriptContentRowRange(contentRowNumbers),
        };
      });
  }, [assignmentResult, scriptRoleBlocks]);

  const assignmentReviewReady = useMemo(() => {
    if (!assignmentResult || !result || assigning) return false;
    if (
      assignmentResult.spreadsheetToken !== result.spreadsheetToken ||
      assignmentResult.sheetId !== result.sheetId ||
      assignmentResult.group !== selectedGroup
    ) {
      return false;
    }
    const roleIds = new Set(assignmentResult.tempRoles.map((role) => role.tempRoleId));
    const assignmentIds = new Set(
      assignmentResult.assignments.map((assignment) => assignment.tempRoleId)
    );
    return (
      roleIds.size > 0 &&
      roleIds.size === assignmentResult.tempRoles.length &&
      assignmentIds.size > 0
    );
  }, [assignmentResult, result, assigning, selectedGroup]);

  const reviewSourceFingerprint = useMemo(() => {
    if (!assignmentResult) return null;
    return buildScriptAssignmentCacheLookup({
      spreadsheetToken: assignmentResult.spreadsheetToken,
      sheetId: assignmentResult.sheetId,
      group: assignmentResult.group,
      parsedRows: allParsedRows,
      overwriteMode: assignmentResult.overwriteMode,
    }).sourceFingerprint;
  }, [allParsedRows, assignmentResult]);

  const assignmentReviewKey = useMemo(() => {
    if (!assignmentReviewReady || !assignmentResult || !reviewSourceFingerprint) return null;
    return [
      assignmentResult.spreadsheetToken,
      assignmentResult.sheetId,
      assignmentResult.group,
      reviewSourceFingerprint,
      assignmentResult.assignments
        .map((assignment) => `${assignment.tempRoleId}:${assignment.wxid}`)
        .sort()
        .join("|"),
    ].join(":");
  }, [assignmentResult, assignmentReviewReady, reviewSourceFingerprint]);

  useEffect(() => {
    if (!assignmentReviewKey || reviewReceipt) return;
    if (autoOpenedReviewKeyRef.current === assignmentReviewKey) return;
    autoOpenedReviewKeyRef.current = assignmentReviewKey;
    setReviewOpen(true);
  }, [assignmentReviewKey, reviewReceipt]);

  const tableData = useMemo<ScriptSheetTableRow[]>(() => {
    const blockByTempRoleId = Object.fromEntries(
      scriptRoleBlocks.map((block) => [block.tempRoleId, block])
    );

    return activePreviewRows.map((row) => {
      const tempRoleId = rowEligibility.get(row.rowIndex)?.tempRoleId;
      const block = tempRoleId ? blockByTempRoleId[tempRoleId] : undefined;
      const blockLabel = block?.bMergeRange
        ? `B${block.bMergeRange}`
        : block
          ? `行${block.startRowNumber}-${block.endRowNumber}`
          : undefined;

      return {
        key: String(row.rowIndex),
        __row: String(row.rowNumber),
        role: row.role,
        assignedRole:
          "assignedRole" in row && typeof row.assignedRole === "string"
            ? row.assignedRole
            : undefined,
        roleIsInherited: row.roleIsInherited,
        tempRoleId,
        blockLabel,
        cellImages: row.cellImages,
        ...row.cells,
      };
    });
  }, [activePreviewRows, rowEligibility, scriptRoleBlocks]);

  useEffect(() => {
    setTablePagination((prev) => ({ ...prev, current: 1 }));
  }, [result, showAssignedPreview]);

  if (credentialsLoading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", minHeight: 300, alignItems: "center" }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div className="fade-in">
      {!hasCredentials && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          title="尚未配置飞书开放平台凭证"
          description={
            isAdmin
              ? "请先在系统设置中填写 App ID 和 App Secret，并在目标表格中添加文档应用授权。"
              : "请联系管理员在系统设置中配置飞书开放平台 App ID 和 App Secret，并在目标表格中添加文档应用授权。"
          }
        />
      )}

      {!hasImageUnderstandingModel && (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          title="尚未配置图片理解模型"
          description={
            isAdmin
              ? "请先在模型管理中设置「图片理解使用中」的视觉模型（建议 GPT-4o 系列）。"
              : "请联系管理员在模型管理中设置「图片理解使用中」的视觉模型（建议 GPT-4o 系列）。"
          }
        />
      )}

      {!isAdmin && scriptAccess.feishuScriptEnabled && scriptAccess.atmosphereGroups.length === 0 && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          title="需先授权气氛员分组"
          description="请联系管理员在「用户与权限」中为您授权气氛员分组后，才能进行角色匹配。"
        />
      )}

      <Card
        variant="borderless"
        className="shadow-sm"
        title={
          <Space>
            <FileTextOutlined style={{ color: "#6366f1" }} />
            <span>飞书剧本</span>
          </Space>
        }
      >
        <Paragraph type="secondary">
          粘贴飞书电子表格链接读取剧本。A 列不展示；B 列为角色并向下继承；仅展示 C–D 列数据；仅展示以「
          {SCRIPT_ATMOSPHERE_ROLE_PREFIX}」开头或角色为「{SCRIPT_DISPATCHER_ROLE}」且 C
          列需发内容非空的行。默认分配时 D 列已有编号的气氛员行（及其同块继承行）不参与匹配；开启覆盖模式后将重新匹配并写回覆盖。
        </Paragraph>

        <Space.Compact style={{ width: "100%", marginBottom: 16 }}>
          <Input
            placeholder="https://xxx.feishu.cn/sheets/xxx?sheet=xxx"
            value={url}
            onChange={(event) => setUrl(event.target.value)}
            onPressEnter={handleRead}
            size="large"
            allowClear
            disabled={assigning || reviewWriting}
          />
          <Button
            type="primary"
            size="large"
            icon={<ReloadOutlined />}
            loading={loading}
            onClick={handleRead}
            disabled={!hasCredentials || assigning || reviewWriting}
          >
            读取
          </Button>
        </Space.Compact>

        <Space wrap style={{ width: "100%", marginBottom: 16 }}>
          <Select
            allowClear
            showSearch
            placeholder="选择气氛员分组"
            style={{ minWidth: 240 }}
            value={selectedGroup}
            loading={groupsLoading}
            disabled={!hasCredentials || groupsLoading || assigning || reviewWriting}
            onChange={(value) => setSelectedGroup(value)}
            options={groups.map((group) => ({ label: group, value: group }))}
            notFoundContent={
              groupsLoading ? <Spin size="small" /> : "暂无分组，请先在气氛员管理中设置"
            }
          />
          <Space size={4}>
            <Switch
              checked={overwriteMode}
              disabled={assigning || imageProgress.running || reviewWriting || Boolean(assignmentResult)}
              onChange={setOverwriteMode}
            />
            <Text type="secondary">覆盖模式</Text>
          </Space>
          <Button
            type="primary"
            icon={<UserSwitchOutlined />}
            loading={assigning}
            disabled={
              !hasCredentials ||
              !result ||
              !selectedGroup ||
              assigning ||
              reviewWriting ||
              imageProgress.running ||
              parsedRows.length === 0
            }
            onClick={handleAssignRoles}
          >
            一键分配
          </Button>
          {assigning || assignmentProgress.total > 0 ? (
            <Alert
              type={assignmentOverallStatus.alertType}
              showIcon
              style={{ flex: "1 1 100%", marginBottom: 0 }}
              title={
                <Space wrap>
                  <Text strong>{assignmentOverallStatus.title}</Text>
                  {assignmentOverallStatus.isLive ? <Badge status="processing" text="运行中" /> : null}
                </Space>
              }
              description={
                <Space vertical size={2}>
                  <Text>{assignmentOverallStatus.alertMessage}</Text>
                  {assignmentOverallStatus.alertDescription ? (
                    <Text type="secondary">{assignmentOverallStatus.alertDescription}</Text>
                  ) : null}
                </Space>
              }
            />
          ) : null}
          {assigning && !assignmentProgress.open ? (
            <Button type="link" onClick={tryReopenActiveAssignmentProgress}>
              查看进度
            </Button>
          ) : null}
          {!assigning && !assignmentProgress.open && assignmentProgress.total > 0 ? (
            <Button type="link" onClick={openAssignmentProgressModal}>
              查看进度
            </Button>
          ) : null}
          {assignmentResumeState ? (
            assignmentResumeState.personasComplete ? (
              <Button
                type="primary"
                icon={<UserSwitchOutlined />}
                loading={assigning}
                disabled={assigning || imageProgress.running || reviewWriting}
                onClick={handleStartMatching}
              >
                开始匹配（已匹配 {assignmentResumeState.matchCount} /{" "}
                {assignmentResumeState.total}）
              </Button>
            ) : (
              <Button
                icon={<UserSwitchOutlined />}
                loading={assigning}
                disabled={assigning || imageProgress.running || reviewWriting}
                onClick={handleResumeAssignRoles}
              >
                继续画像（{assignmentResumeState.personaCount} / {assignmentResumeState.total}）
              </Button>
            )
          ) : null}
          {hasAssignmentCache ? (
            <Popconfirm
              title="清除画像与匹配缓存？"
              description="将删除服务端画像/匹配结果、取消后台匹配任务，并重置本页进度。图片理解缓存保留。"
              okText="清除"
              cancelText="取消"
              okButtonProps={{ danger: true }}
              onConfirm={() => void handleClearAssignmentCache()}
            >
              <Button danger type="text" disabled={assigning || reviewWriting}>
                清除缓存
              </Button>
            </Popconfirm>
          ) : null}
          {!groupsLoading && groups.length > 0 ? (
            <Text type="secondary">共 {groups.length} 个分组</Text>
          ) : null}
        </Space>

        {result && (
          <>
            <Descriptions
              size="small"
              bordered
              column={{ xs: 1, sm: 2, md: 3 }}
              style={{ marginBottom: 16 }}
              items={[
                { key: "title", label: "工作表", children: result.sheetTitle },
                { key: "token", label: "spreadsheetToken", children: result.spreadsheetToken },
                { key: "sheetId", label: "sheetId", children: result.sheetId },
                { key: "range", label: "读取范围", children: result.range },
                {
                  key: "size",
                  label: "规模",
                  children: `展示 ${parsedRows.length} 行（共 ${totalParsedRows} 行，已按角色与需发内容过滤）`,
                },
              ]}
            />

            <Space wrap style={{ marginBottom: 16 }}>
              <Button
                type="default"
                icon={<ThunderboltOutlined />}
                onClick={handleUnderstandImages}
                loading={imageProgress.running}
                disabled={
                  !hasImageUnderstandingModel ||
                  assignmentContentImages.length === 0 ||
                  assigning ||
                  reviewWriting ||
                  imageProgress.running
                }
              >
                图片理解（C列）
                {assignmentContentImages.length > 0 ? ` (${assignmentContentImages.length})` : ""}
              </Button>
              {assignmentContentImages.length > 0 && (
                <Text type="secondary">
                  匹配所需 C 列图片，并发 {IMAGE_UNDERSTANDING_CONCURRENCY}，已缓存将直接复用
                </Text>
              )}
            </Space>

            {assignmentResult && (
              <Card
                size="small"
                title={
                  assignmentProgress.running && assignmentProgress.phase === "matching"
                    ? `分配结果（${assignmentResult.assignments.length}/${assignmentProgress.total}${
                        formatSelectionSourceSummary(assignmentResult.assignments)
                          ? ` · ${formatSelectionSourceSummary(assignmentResult.assignments)}`
                          : ""
                      }）`
                    : assignmentResult.assignments.length > 0
                      ? `分配结果${
                          formatSelectionSourceSummary(assignmentResult.assignments)
                            ? `（${formatSelectionSourceSummary(assignmentResult.assignments)}）`
                            : ""
                        }`
                      : "分配结果"
                }
                style={{ marginBottom: 16 }}
                extra={
                  <Space>
                    <Button
                      size="small"
                      onClick={() => setShowAssignedPreview((value) => !value)}
                    >
                      {showAssignedPreview ? "查看原始角色" : "查看分配预览"}
                    </Button>
                    {reviewReceipt ? (
                      <Tag color="success">已写回 {reviewReceipt.updatedCells} 行</Tag>
                    ) : (
                      <Button
                        type="primary"
                        size="small"
                        disabled={!assignmentReviewReady || !reviewSourceFingerprint}
                        onClick={() => setReviewOpen(true)}
                      >
                        审查并写入飞书
                      </Button>
                    )}
                  </Space>
                }
              >
                <Table
                  size="small"
                  pagination={false}
                  rowKey="tempRoleId"
                  dataSource={assignmentTableRows}
                  scroll={{ x: "max-content" }}
                  expandable={{
                    expandedRowRender: (record) => renderScoreBreakdownPanel(record),
                    rowExpandable: (record) => (record.scoreBreakdown?.length ?? 0) > 0,
                  }}
                  columns={[
                    {
                      title: "临时角色",
                      dataIndex: "tempRoleId",
                      width: 96,
                      render: (value: string) => `气氛员${value}`,
                    },
                    {
                      title: "内容类型",
                      dataIndex: "contentBeat",
                      width: 88,
                      render: (value?: ScriptContentBeatType) =>
                        value ? getScriptContentBeatLabel(value) : "—",
                    },
                    {
                      title: "话术行",
                      dataIndex: "contentRowRange",
                      width: 108,
                      render: (value?: string) =>
                        value && value !== "—" ? (
                          <Text style={{ fontSize: 12 }}>{value}</Text>
                        ) : (
                          "—"
                        ),
                    },
                    {
                      title: "气氛员编号",
                      key: "number",
                      width: 120,
                      render: (_: unknown, record) =>
                        renderAssignedMemberLabel({
                          number: record.number,
                          nickname: record.nickname,
                        }),
                    },
                    {
                      title: "选法",
                      key: "selectionSource",
                      width: 96,
                      render: (_: unknown, record: AssignmentTableRow) =>
                        renderSelectionSourceTag(record.selectionSource),
                    },
                    {
                      title: "原话",
                      dataIndex: "sampleQuotes",
                      width: 280,
                      render: (quotes: string[]) => renderSampleQuotes(quotes),
                    },
                    {
                      title: "总分",
                      dataIndex: "score",
                      width: 80,
                      render: (value: number) => <Tag color="blue">{value}</Tag>,
                    },
                    { title: "总评", dataIndex: "reason", ellipsis: true },
                  ]}
                />
              </Card>
            )}

            <Table
              size="small"
              bordered
              columns={tableColumns}
              dataSource={tableData}
              pagination={{
                current: tablePagination.current,
                pageSize: tablePagination.pageSize,
                showSizeChanger: true,
                pageSizeOptions: [20, 50, 100, 200],
                showTotal: (total) => `共 ${total} 行`,
                onChange: (page, pageSize) => {
                  setTablePagination({ current: page, pageSize });
                },
              }}
              scroll={{ x: "max-content" }}
            />

            {complexCellDebug.length > 0 && (
              <Collapse
                style={{ marginTop: 16 }}
                items={[
                  {
                    key: "complex-cells",
                    label: `复杂单元格原始 JSON（${complexCellDebug.length} 个）`,
                    children: (
                      <Space vertical size="middle" style={{ width: "100%" }}>
                        {complexCellDebug.map((item) => (
                          <div key={item.key}>
                            <Text strong>
                              第 {item.row} 行 · {item.column}
                            </Text>
                            <div style={{ marginTop: 4, marginBottom: 4 }}>
                              <Text type="secondary">解析后：{item.display || "—"}</Text>
                            </div>
                            <pre
                              style={{
                                margin: 0,
                                padding: 12,
                                background: "#f8fafc",
                                borderRadius: 8,
                                overflow: "auto",
                                fontSize: 12,
                              }}
                            >
                              {item.json}
                            </pre>
                          </div>
                        ))}
                      </Space>
                    ),
                  },
                ]}
              />
            )}
          </>
        )}

        {!result && (
          <Text type="secondary">读取成功后，将按角色解析规则展示表格数据。</Text>
        )}
      </Card>

      <Modal
        title="图片理解进度"
        open={imageProgress.open}
        closable={!imageProgress.running}
        mask={{ closable: false }}
        onCancel={() => {
          if (!imageProgress.running) {
            setImageProgress(INITIAL_IMAGE_PROGRESS);
          }
        }}
        footer={
          <Button
            disabled={imageProgress.running}
            onClick={() => setImageProgress(INITIAL_IMAGE_PROGRESS)}
          >
            {imageProgress.running ? "处理中..." : "关闭"}
          </Button>
        }
        width={520}
      >
        <Space vertical style={{ width: "100%" }} size="middle">
          <Progress
            percent={
              imageProgress.total > 0
                ? Math.round((imageProgress.done / imageProgress.total) * 100)
                : 0
            }
            status={imageProgress.running ? "active" : "normal"}
          />
          <Text>
            进度：{imageProgress.done} / {imageProgress.total}
          </Text>
          <Text>
            成功 <Text type="success">{imageProgress.success}</Text>，失败{" "}
            <Text type="danger">{imageProgress.fail}</Text>
          </Text>
          {imageProgress.running && imageProgress.currentLabel ? (
            <Text type="secondary">正在处理：{imageProgress.currentLabel}</Text>
          ) : null}
          {imageProgress.failures.length > 0 && !imageProgress.running ? (
            <div style={{ maxHeight: 160, overflow: "auto" }}>
              {imageProgress.failures.map((item) => (
                <div key={item.fileToken} style={{ marginBottom: 8 }}>
                  <Text strong>{item.label}</Text>
                  <div>{item.error}</div>
                </div>
              ))}
            </div>
          ) : null}
        </Space>
      </Modal>

      <Modal
        title={
          <Space>
            <span>气氛员分配进度</span>
            <Tag color={assignmentOverallStatus.isLive ? "processing" : "default"}>
              {assignmentOverallStatus.title}
            </Tag>
          </Space>
        }
        open={assignmentProgress.open}
        closable
        mask={{ closable: false }}
        onCancel={closeAssignmentProgressModal}
        footer={
          <Space>
            {assignmentPersonaErrorCount > 0 &&
            !assignmentProgress.running &&
            !assigning &&
            !personaRetryRunning ? (
              <Button
                type="primary"
                loading={personaRetryRunning}
                onClick={() => void handleRetryAllFailedPersonas()}
              >
                重试全部失败（{assignmentPersonaErrorCount}）
              </Button>
            ) : null}
            {assignmentProgress.total > 0 &&
            assignmentProgress.personaDone >= assignmentProgress.total &&
            assignmentPersonaErrorCount === 0 &&
            !assignmentProgress.running &&
            !personaRetryRunning &&
            assignmentProgress.matchDone < assignmentProgress.total ? (
              <Button
                type="primary"
                loading={assigning}
                onClick={handleStartMatching}
              >
                开始匹配
              </Button>
            ) : null}
            <Button onClick={closeAssignmentProgressModal}>
              {assignmentProgress.running ||
              assigning ||
              personaRetryRunning ||
              hasResumableAssignmentProgress(assignmentProgress)
                ? "收起"
                : "关闭"}
            </Button>
          </Space>
        }
        width={960}
      >
        <Space vertical style={{ width: "100%" }} size="middle">
          <Alert
            type={assignmentOverallStatus.alertType}
            showIcon
            title={assignmentOverallStatus.alertMessage}
            description={assignmentOverallStatus.alertDescription}
          />
          <Descriptions
            size="small"
            bordered
            column={{ xs: 1, sm: 2, md: 4 }}
            items={[
              {
                key: "run",
                label: "运行状态",
                children: assignmentOverallStatus.isLive ? (
                  <Badge status="processing" text="运行中" />
                ) : assignmentProgress.phase === "done" &&
                  assignmentProgress.matchDone >= assignmentProgress.total ? (
                  <Badge status="success" text="已完成" />
                ) : (
                  <Badge status="default" text="已暂停" />
                ),
              },
              {
                key: "step",
                label: "当前阶段",
                children:
                  {
                    idle: "未开始",
                    prepare: "准备数据",
                    persona: "画像分析",
                    matching: "角色匹配",
                    done: "已完成",
                  }[assignmentProgress.phase] ?? "—",
              },
              {
                key: "persona",
                label: "画像进度",
                children: `${assignmentProgress.personaDone} / ${assignmentProgress.total}`,
              },
              {
                key: "match",
                label: "匹配进度",
                children: formatMatchProgressSummary(assignmentProgress),
              },
            ]}
          />
          {renderAssignmentTokenUsage(assignmentProgress.tokenUsage)}
          <div>
            <Text type="secondary">画像分析</Text>
            <Progress
              percent={
                assignmentProgress.total > 0
                  ? Math.round((assignmentProgress.personaDone / assignmentProgress.total) * 100)
                  : 0
              }
              status={assignmentPersonaProgressStatus}
              size="small"
            />
            <Text>
              画像进度：{assignmentProgress.personaDone} / {assignmentProgress.total}
            </Text>
          </div>
          <div>
            <Text type="secondary">角色匹配</Text>
            <Progress
              percent={computeMatchProgressPercent(assignmentProgress)}
              status={assignmentMatchProgressStatus}
              size="small"
            />
            <Text>
              匹配进度：{formatMatchProgressSummary(assignmentProgress)}
              {assignmentProgress.memberCount > 0
                ? `（分组内 ${assignmentProgress.memberCount} 名气氛员）`
                : ""}
            </Text>
          </div>
          {assignmentCurrentTaskLabel ? (
            <Alert
              type={assignmentProgress.running || assigning ? "info" : "warning"}
              showIcon={assignmentProgress.running || assigning}
              title={
                assignmentProgress.running || assigning ? "当前任务" : "最近状态"
              }
              description={assignmentCurrentTaskLabel}
              style={{ marginBottom: 0 }}
            />
          ) : null}
          {assignmentProgress.phase === "matching" ? (
            <Alert
              type={
                assignmentProgress.matchPoll.lastError ||
                assignmentProgress.matchPoll.startStatus === "failed"
                  ? "warning"
                  : "info"
              }
              showIcon
              title="进度轮询"
              description={
                <Space vertical size={2}>
                  <Text>
                    启动：
                    {assignmentProgress.matchPoll.startStatus === "accepted"
                      ? "已受理"
                      : assignmentProgress.matchPoll.startStatus === "pending"
                        ? "请求中…"
                        : assignmentProgress.matchPoll.startStatus === "failed"
                          ? `失败（${assignmentProgress.matchPoll.startError ?? "未知"}）`
                          : "未开始"}
                  </Text>
                  <Text>
                    轮询：{assignmentProgress.matchPoll.count} 次
                    {assignmentProgress.matchPoll.lastAt
                      ? ` · 最近 ${new Date(assignmentProgress.matchPoll.lastAt).toLocaleTimeString("zh-CN")}`
                      : ""}
                  </Text>
                  <Text>
                    服务端心跳：
                    {assignmentProgress.matchLastHeartbeatAt
                      ? new Date(assignmentProgress.matchLastHeartbeatAt).toLocaleTimeString("zh-CN")
                      : "暂无（任务可能未入队）"}
                  </Text>
                  {assignmentProgress.matchPoll.lastError ? (
                    <Text type="danger">{assignmentProgress.matchPoll.lastError}</Text>
                  ) : null}
                  <Text type="secondary">接口：getRoleAssignmentMatchProgress（轻量）</Text>
                  <Text type="secondary">
                    匹配由 BullMQ 队列执行；[script-role-match] 日志在服务端进程 stdout，不在浏览器控制台
                  </Text>
                </Space>
              }
            />
          ) : null}
          {primaryBlockDiagnostics ? (
            <Alert
              type="error"
              showIcon
              title={`匹配阻塞：气氛员${primaryBlockDiagnostics.blockedTempRoleId}`}
              description={
                <GlobalAssignmentBlockDetail
                  diagnostics={primaryBlockDiagnostics}
                  compact
                />
              }
              style={{ marginBottom: 0 }}
            />
          ) : null}
          <Table
            size="small"
            pagination={false}
            rowKey="tempRoleId"
            scroll={{ y: 360 }}
            dataSource={assignmentProgress.items}
            onRow={(record) => ({
              style: isAssignmentRowActive(record, assignmentProgress, assigning)
                ? { backgroundColor: "var(--ant-color-primary-bg, #e6f4ff)" }
                : undefined,
            })}
            columns={[
              {
                title: "临时角色",
                dataIndex: "tempRoleId",
                width: 96,
                render: (value: string) => `气氛员${value}`,
              },
              {
                title: "发言数",
                dataIndex: "utteranceCount",
                width: 72,
              },
              {
                title: "内容类型",
                dataIndex: "contentBeat",
                width: 88,
                render: (value: ScriptContentBeatType) => getScriptContentBeatLabel(value),
              },
              {
                title: "话术行",
                dataIndex: "contentRowRange",
                width: 108,
              },
              {
                title: "状态",
                dataIndex: "status",
                width: 148,
                render: (_: AssignmentItemStatus, record: AssignmentProgressItem) =>
                  renderAssignmentItemStatus(record, assignmentProgress, assigning),
              },
              {
                title: "原话",
                key: "sampleQuotes",
                width: 240,
                render: (_: unknown, record: AssignmentProgressItem) =>
                  renderSampleQuotes(record.persona?.sampleQuotes),
              },
              {
                title: "匹配结果",
                key: "result",
                width: 200,
                render: (_: unknown, record: AssignmentProgressItem) => {
                  if (record.status === "matching") {
                    const activeSince =
                      record.tempRoleId === assignmentProgress.activeTempRoleId
                        ? assignmentProgress.activeSince
                        : null;
                    return renderMatchingInProgressLabel(activeSince, assignmentNowTick);
                  }
                  if (record.status === "persona") {
                    return (
                      <Space size={4}>
                        <Spin size="small" />
                        <Text type="secondary">画像分析中…</Text>
                      </Space>
                    );
                  }
                  if (record.assignment) {
                    return (
                      <span>
                        {renderAssignedMemberLabel({
                          number: record.assignment.number,
                          nickname: record.assignment.nickname,
                        })}
                        <Tag color="blue" style={{ marginLeft: 8 }}>
                          {record.assignment.score} 分
                        </Tag>
                      </span>
                    );
                  }
                  if (record.error || record.blockDiagnostics) {
                    return renderMatchFailureDetail(record);
                  }
                  return "—";
                },
              },
              {
                title: "选法",
                key: "selectionSource",
                width: 96,
                render: (_: unknown, record: AssignmentProgressItem) =>
                  record.assignment
                    ? renderSelectionSourceTag(record.assignment.selectionSource)
                    : "—",
              },
              {
                title: "操作",
                key: "actions",
                width: 96,
                render: (_: unknown, record: AssignmentProgressItem) => {
                  if (
                    record.status === "error" &&
                    !shouldUseInlinePersona(record.utteranceCount) &&
                    !record.assignment &&
                    !record.persona
                  ) {
                    return (
                      <Button
                        type="link"
                        size="small"
                        loading={retryingPersonaTempRoleId === record.tempRoleId}
                        disabled={
                          personaRetryRunning &&
                          retryingPersonaTempRoleId !== record.tempRoleId
                        }
                        onClick={() => void handleRetryPersona(record.tempRoleId)}
                      >
                        重试画像
                      </Button>
                    );
                  }
                  return "—";
                },
              },
            ]}
          />
        </Space>
      </Modal>

      {assignmentResult && reviewSourceFingerprint ? (
        <ScriptAssignmentReviewPanel
          open={reviewOpen}
          assignmentResult={assignmentResult}
          parsedRows={allParsedRows}
          sourceFingerprint={reviewSourceFingerprint}
          sheetTitle={result?.sheetTitle ?? ""}
          overwriteMode={assignmentResult.overwriteMode}
          imageTextsByFileToken={assignmentResult.imageTextsByFileToken}
          onClose={() => setReviewOpen(false)}
          onConfirmStart={() => setReviewWriting(true)}
          onConfirmed={handleReviewConfirmed}
          onConfirmError={handleReviewConfirmError}
        />
      ) : null}
    </div>
  );
}
